import React from 'react';
import _, { capitalize } from 'lodash';
// material
import {
  Box,
  Card,
  Button,
  Grid,
  TextField,
  Typography,
  Menu,
  MenuItem,
  Divider,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  Chip,
  Skeleton,
  Tooltip,
  Link,
  Checkbox,
  FormControlLabel,
  LinearProgress,
  useMediaQuery,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import {
  VerifiedUser,
  ChevronLeft,
  CopyAll,
  Edit,
  MoreVert,
  LocalPhoneOutlined,
  HomeOutlined,
  LockOutlined,
  Add,
  SmsOutlined,
  Inventory2Rounded,
  MonitorWeightOutlined,
  PlayArrowRounded,
  RefreshRounded,
  SwapHorizRounded,
  TuneRounded,
  CreateRounded,
  CloseRounded,
  AddAPhotoRounded,
  EventBusyRounded,
  EventRepeatRounded,
  Check,
  CheckCircleOutline,
  HourglassEmpty,
  ErrorOutline,
  PersonRemoveRounded,
  TaskAlt,
  OpenInNew,
} from '@mui/icons-material';
import { useHistory, Link as RouterLink } from 'react-router-dom';
import { useSnackbar } from 'notistack';
// components
import GoogleMapMarker from 'src/components/GoogleMaps/GoogleMapMarker';
import GoogleDirectionsMap from 'src/components/GoogleMaps/GoogleDirectionsMap';
import DriverSelectorModal from './DriverSelectorModal';
import CompleteFoodRunModal from '../../FoodRun/CompleteFoodRunModal';
// hooks
import { useEffect, useState, useMemo } from 'react';
import useAuth from 'src/hooks/useAuth';
//
import * as Sentry from '@sentry/react';
import * as backend from 'src/backend';
import { BUCKET_NAME, S3_BASE_URL } from 'src/config';
import { rescues, foodRun, donations, chat } from 'src/backend';
import _isFinite from 'lodash/isFinite';
import _get from 'lodash/get';
import { useCallback } from 'react';
import ConfirmModal from 'src/components/ConfirmModal';
import { PATH_DASHBOARD } from 'src/routes/paths';
import MoreButton from 'src/components/MoreButton';
import {
  ALREADY_CLAIMED_ERROR,
  ONBOARDING_REQUIRED_ERORR,
  useGetVehicleLabels,
} from 'src/constants';
// complianceProfileType removed — no longer shown in inline onboarding card
import { Weight, Temperature, formatDisplayDate } from 'careit';
import StockPhotoDisclaimer from 'src/components/StockPhotoDisclaimer';
import useChatClient from 'src/hooks/useChatClient';
import useDonation from 'src/hooks/useDonation';
import useTeams from 'src/hooks/useTeams';
import {
  getDonationAvailabilityNotice,
  getSwitchableOrganizations,
  isClaimWindowClosed,
} from 'src/utils/donationAvailability';
import DonationUnavailable from './DonationUnavailable';
import DonationAvailabilityNotice from './DonationAvailabilityNotice';
import { MBadge } from 'src/components/@material-extend';
import _noop from 'lodash/noop';
import DonationEventLogs from './DonationEventLogs';
import DonationCancelConfirmationModal from 'src/components/DonationCancelConfirmationModal';
import SocketIO from 'src/backend/SocketIO';
import emitDonationUpdate from 'src/utils/emitDonationUpdate';
import { styled } from '@mui/system';
import { useTheme } from '@mui/material/styles';
import { UploadImagesComponent } from 'src/hooks/useForm';
import { groupByUnit } from 'src/components/FoodRun/FoodRunInfo';
import * as FoodTypesIcons from 'src/components/icons/foodTypes';
import useTeamMembers from 'src/hooks/useTeamMembers';
import useEventBus from 'src/hooks/useEventBus';
import RescueCancelConfirmationModal from 'src/components/RescueCancelConfirmationModal';
import _isNumber from 'lodash/isNumber';
import { Trans, useTranslation } from 'react-i18next';
import moment from 'moment-timezone';
import useDonationStatus from 'src/hooks/useDonationStatus';
import useRecurringType from 'src/hooks/useRecurringType';
import useTrainingComplete from 'src/hooks/useTrainingComplete';
import { getFoodTypeLabel } from 'src/utils/foodTypes';
import { getDeliveryOptionLabel } from 'src/utils/deliveryOptions';
import { useMeasureFieldUnits } from 'src/components/MeasureField';
import useCheckExistingRescue from 'src/hooks/useCheckExistingRescue';
import DialogClaimPartOfPartialRescue from 'src/components/DialogClaimPartOfPartialRescue';
import useGetPartialRescues from 'src/hooks/useGetRescuesByDonationId';
import { useQueryClient } from '@tanstack/react-query';
import { uploadImages } from 'src/components/form/UploadImages';
import DialogCreateDonation from 'src/components/DialogCreateDonation';
import { parseJsonArray, parseJsonObject } from 'src/utils/json';
import {
  getApproximateRadius,
  getHiddenDonorLabel,
  isDonorHidden,
} from 'src/utils/donorPrivacy';
import { canViewDonationOnboardingRequirements } from './donationOnboarding';

export function fWeight(value, donation, id) {
  const unit = donation?.[id + '_unit'] ?? 'lbs';
  return new Weight(new Weight(value ?? 0, 'lbs').as(unit), unit).toString();
}

export function fTemp(value, donation, id) {
  if (!Number.isFinite(value)) {
    return '-';
  }

  const unit = donation?.[id + '_unit'] ?? 'f';
  return new Temperature(new Temperature(value, 'f').as(unit), unit).toString();
}

// ----------------------------------------------------------------------

export const labelColor = '#637381';

// ----------------------------------------------------------------------

const RESCUE_STATUSES_ACCEPTED_FOR_WEIGHTS = [
  'ASSIGNED',
  'STARTED',
  'COMPLETED',
  'RESCUED',
];

// For partial donations, "claimable" means any food type still has unclaimed
// weight on donation_food_type.unreserved_weight (surfaced as the
// unassigned_food_types JSON array).
function hasUnassignedFoodTypes(donation) {
  const unassigned = parseJsonArray(donation?.unassigned_food_types);
  return unassigned.length > 0;
}

// The onboarding requirements panel, scrolled to from the disabled Claim hint.
const DONATION_REQUIREMENTS_ELEMENT_ID = 'donation-onboarding-requirements';

function canClaim(donation, user) {
  const available =
    !donation.rescue_id &&
    (donation.is_partial ? hasUnassignedFoodTypes(donation) : true);
  const canRescue =
    user.team_id !== donation.donor_team_id &&
    !donation.is_food_run &&
    user.account_type !== 'ADMIN';

  return available && canRescue;
}

function canUnclaim(donation, user) {
  return (
    !!donation.rescue_id &&
    donation.rescue_status !== 'COMPLETED' &&
    donation.recipient_team_id === user.team_id &&
    !donation.is_food_run &&
    !user.is_driver
  );
}

function canStartRescue(donation, user) {
  const isDonorTeam = user.team_id === donation.donor_team_id;
  const isRecipientTeam = user.team_id === donation.recipient_team_id;

  if (donation.is_food_run) {
    return false;
  }

  return (
    (donation.delivery_option === 'SELF-DELIVER' &&
      isDonorTeam &&
      donation.rescue_status === 'ASSIGNED') ||
    (donation.delivery_option === 'RECEIVER-PICKUP' &&
      isRecipientTeam &&
      donation.rescue_status === 'ASSIGNED')
  );
}

function canCompleteRescue(donation, user) {
  const isDonorTeam = user.team_id === donation?.donor_team_id;
  const isRecipientTeam = user.team_id === donation?.recipient_team_id;

  if (donation?.is_partial && isDonorTeam) {
    return false;
  }
  if (donation?.is_food_run) {
    return false;
  }

  // Don't allow weights entry if rescue is waiting for approval
  if (donation?.rescue_status === 'WAITING_FOR_APPROVAL') {
    return false;
  }

  // Let past donations be completed by the donor team (Even when the rescue has pending approval status)
  // and recipient team.
  if (
    (isDonorTeam || isRecipientTeam) &&
    moment().isAfter(moment(donation.end_date).add(24, 'hours'))
  ) {
    return donation?.rescue_status !== 'COMPLETED';
  }

  if (donation?.delivery_option === 'SELF-DELIVER') {
    return isRecipientTeam && donation?.rescue_status === 'STARTED';
  }

  if (
    isRecipientTeam &&
    moment().diff(moment(donation?.end_date), 'hours') > 24 &&
    donation?.rescue_status !== 'COMPLETED'
  ) {
    return true;
  }
  return isRecipientTeam && donation?.rescue_status === 'STARTED';
}

function canAcceptRescue(donation, user) {
  const isRecipientTeam = user.team_id === donation.recipient_team_id;
  if (donation.delivery_option === 'SELF-DELIVER' && isRecipientTeam) {
    return donation.rescue_status == 'WAITING_FOR_APPROVAL';
  }
  return (
    isRecipientTeam &&
    (user.is_driver == 0 ||
      (user.is_driver == 1 &&
        donation.rescue_status === 'WAITING_FOR_APPROVAL')) &&
    (donation.rescue_status === 'WAITING_FOR_APPROVAL' ||
      donation.rescue_status === 'ASSIGNED')
  );
}

/**
 * Onboarding the user's org still needs before it can rescue this donation.
 * Looked up by donation, since a nonprofit doesn't see the donor's team
 * until it claims.
 */
function useRequiredOnobardingItems(donationId) {
  const [cache, setCache] = useState(null);
  const [valid, setValid] = useState(false);
  const [requirementsKnown, setRequirementsKnown] = useState(false);
  const [isLoading, setLoading] = useState(true);
  const { t } = useTranslation();

  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    setCache(null);
    setRequirementsKnown(false);
    setValid(false);
  }, [donationId]);

  const refresh = useCallback(async () => {
    if (!donationId) {
      setCache(null);
      setRequirementsKnown(true);
      setLoading(false);
      setValid(true);
      return null;
    }

    setCache(null);
    setRequirementsKnown(false);
    setLoading(true);

    try {
      const data =
        await backend.team.getItemsRequiredToRescueDonation(donationId);
      setCache(data);
      setRequirementsKnown(true);
      setValid(true);
      return data;
    } catch (err) {
      console.error(err);
      setCache(null);
      setRequirementsKnown(false);
      enqueueSnackbar(t('donations.rescue-eligibility-check-error'), {
        variant: 'error',
      });
      setValid(true);
      return null;
    } finally {
      setLoading(false);
    }
  }, [enqueueSnackbar, donationId, t]);

  useEffect(() => {
    if (valid) {
      return;
    }

    refresh();
  }, [refresh, valid]);

  const invalidate = useCallback(() => setValid(false), []);
  const requiredItems = cache?.requiredItems ?? [];
  const fallbackOnboardingGroups = requiredItems.reduce((groups, item) => {
    const groupKey = `${item.compliance_profile_id ?? ''}:${item.corporate_team_id ?? ''}`;
    const existingGroup = groups[groupKey];

    if (existingGroup) {
      existingGroup.items.push(item);
      existingGroup.requiredItemCount = existingGroup.items.length;
      existingGroup.hasOnboardingRequest =
        existingGroup.hasOnboardingRequest ||
        Boolean(item.has_onboarding_request);
      return groups;
    }

    groups[groupKey] = {
      complianceProfileId: item.compliance_profile_id,
      complianceProfileTitle: item.compliance_profile_title ?? '',
      complianceProfileType: item.compliance_profile_type ?? item.type ?? '',
      gatekeeperTeamId: item.corporate_team_id,
      gatekeeperName: item.onboarding_manager_name ?? '',
      gatekeeperType: item.gatekeeper_type ?? 'CORPORATE',
      hasOnboardingRequest: Boolean(item.has_onboarding_request),
      hasIncomingOnboardingRequest: Boolean(
        cache?.hasIncomingOnboardingRequest
      ),
      requiredItemCount: 1,
      items: [item],
    };

    return groups;
  }, {});
  const onboardingGroups =
    cache?.onboardingGroups ?? Object.values(fallbackOnboardingGroups);

  return {
    requiredItems,
    onboardingGroups,
    requirementsKnown,
    isLoading,
    invalidate,
    refresh,
  };
}

// ----------------------------------------------------------------------

function AcceptDonation({
  onAccept,
  donation,
  loading,
  trainingComplete = true,
  size = 'medium',
  variant = 'contained',
  sx,
}) {
  const { t } = useTranslation('translation');
  const [open, setOpen] = useState(false);
  const [isOpening, setIsOpening] = useState(false);
  const [dialogDonation, setDialogDonation] = useState(donation);
  const handleOpen = useCallback(async () => {
    setIsOpening(true);
    try {
      const freshDonation = donation?.id
        ? await donations.getById(donation.id, {
            rescueId: donation.rescue_id,
          })
        : null;
      setDialogDonation(freshDonation || donation);
    } catch (err) {
      console.error(err);
      setDialogDonation(donation);
    } finally {
      setOpen(true);
      setIsOpening(false);
    }
  }, [donation]);
  const handleClose = useCallback(() => setOpen(false), []);
  const { user } = useAuth();
  const [incomingRequest, setIncomingRequest] = useState(null);

  useEffect(() => {
    let isMounted = true;
    if (donation?.is_recurring && donation?.id) {
      backend.rescues
        .getIncomingRequestStatus(donation.id)
        .then((data) => {
          if (isMounted) setIncomingRequest(data);
        })
        .catch(() => {})
        .finally(() => {});
    } else {
      setIncomingRequest(null);
    }
    return () => {
      isMounted = false;
    };
  }, [donation?.id, donation?.is_recurring]);

  const handleDriverAssign = useCallback(
    async ({
      recipientTeamId,
      pickupInstructions,
      dropoffInstructions,
      convertSingleDonation,
      drivers,
    }) => {
      handleClose();
      try {
        await onAccept({
          drivers,
          recipientTeamId,
          pickupInstructions,
          dropoffInstructions,
          convertSingleDonation,
          isPartialRescue: !!donation.is_partial,
        });
      } catch (err) {
        console.error(err);
        Sentry.captureException(err);
      }
    },
    [handleClose, onAccept, donation.is_partial]
  );

  const handleAcceptByDriver = useCallback(async () => {
    try {
      await onAccept({
        isPartialRescue: !!donation.is_partial,
      });
    } catch (err) {
      console.error(err);
      Sentry.captureException(err);
    }
  }, [donation, user, onAccept]);

  const trainingTooltip = trainingComplete
    ? undefined
    : t('training-program.complete-training-before-accepting');

  return (
    <>
      <Tooltip title={trainingTooltip}>
        <span>
          <LoadingButton
            startIcon={
              donation.rescue_status === 'ASSIGNED' ? (
                <SwapHorizRounded />
              ) : (
                <Check />
              )
            }
            color="primary"
            onClick={
              user.is_driver ||
              (donation.delivery_option === 'SELF-DELIVER' &&
                user.team_id === donation.recipient_team_id)
                ? handleAcceptByDriver
                : handleOpen
            }
            loading={loading || isOpening}
            disabled={!trainingComplete}
            size={size}
            variant={variant}
            sx={{ fontWeight: 800, ...sx }}
          >
            {donation.rescue_status === 'ASSIGNED'
              ? t('donations.re-assign-rescue')
              : donation?.is_recurring && incomingRequest?.is_bulk_inserted
                ? t('donations.accept-recurring-rescue-request')
                : t('donations.accept-rescue-request')}
          </LoadingButton>
        </span>
      </Tooltip>

      {open && (
        <DriverSelectorModal
          canConvertToFoodRun={donation.rescue_status !== 'ASSIGNED'}
          donation={dialogDonation || donation}
          open={open}
          onClose={handleClose}
          handleDriverAssign={handleDriverAssign}
        />
      )}
    </>
  );
}

function ClaimDonation({ donationId, donation, onClaim, disabled }) {
  const { t } = useTranslation('translation');
  const { user } = useAuth();
  const { activeMembers } = useTeamMembers();
  const history = useHistory();

  const { enqueueSnackbar } = useSnackbar();

  const [portionDetails, setPortionDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const handleOpen = useCallback(() => setOpen(true), []);
  const handleClose = useCallback(() => {
    setOpen(false);
    setPortionDetails(null);
  }, []);
  const [openDialogClaimPortion, setOpenDialogClaimPortion] = useState(false);
  const [existingPartialRescue, setExistingPartialRescue] = useState(null);
  const {
    mutateAsync: checkForExistingRescue,
    isLoading: isLoadingCheckForExistingRescue,
  } = useCheckExistingRescue();
  const hasExistingPartialClaim = Boolean(existingPartialRescue?.rescue_id);
  const claimOpensDriverModal = !(
    (donation.delivery_option === 'SELF-DELIVER' || user.is_driver) &&
    !donation.is_food_run
  );

  useEffect(() => {
    let isMounted = true;

    if (!donation?.is_partial || !donation?.id || !user?.team_id) {
      setExistingPartialRescue(null);
      return undefined;
    }

    checkForExistingRescue({
      donationId: donation.id,
      recipientTeamId: user.team_id,
    })
      .then((existingRescue) => {
        if (isMounted) {
          setExistingPartialRescue(existingRescue?.rescue || null);
        }
      })
      .catch(() => {
        if (isMounted) {
          setExistingPartialRescue(null);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [
    checkForExistingRescue,
    donation?.id,
    donation?.is_partial,
    user?.team_id,
  ]);

  const handleDriverAssign = useCallback(
    async ({
      recipientTeamId,
      pickupInstructions,
      dropoffInstructions,
      convertSingleDonation,
      drivers,
    }) => {
      handleClose();
      setLoading(true);

      if (recipientTeamId) {
        try {
          await donations.convertToFoodRun({
            recipientTeamId,
            parentId: donation.parent_id,
            donorTeamId: donation.donor_team_id,
            pickupInstructions,
            dropoffInstructions,
            donationId: convertSingleDonation ? donation.id : null,
            drivers,
          });
          history.push(PATH_DASHBOARD.viewFoodRun(donationId));
        } catch (err) {
          console.error(err);
          enqueueSnackbar(err.message, { variant: 'error' });
        } finally {
          setLoading(false);
        }
        return;
      }

      try {
        const createdRescueId = await rescues.create({
          donationId,
          user,
          drivers,
          isPartialRescue: donation.is_partial,
          partialClaims: portionDetails?.partialClaims,
          replaceExistingPartialClaims:
            portionDetails?.replaceExistingPartialClaims,
        });
        if (donation.is_partial) {
          history.push(
            PATH_DASHBOARD.viewPartialDonation(donationId, createdRescueId)
          );
        }
        enqueueSnackbar(t('donations.donation-rescued'), {
          variant: 'success',
        });
        onClaim();
      } catch (err) {
        if (err.message === ONBOARDING_REQUIRED_ERORR) {
          enqueueSnackbar(t('donations.onboarding-required-error'), {
            variant: 'error',
          });
          return;
        } else if (err.message === ALREADY_CLAIMED_ERROR) {
          enqueueSnackbar(t('donations.already-claimed-error'), {
            variant: 'error',
          });
          return;
        }

        enqueueSnackbar(err.message, {
          variant: 'error',
        });
      }

      setLoading(false);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [handleClose, donationId, user, activeMembers, portionDetails]
  );

  const isSelfDeliver = donation.delivery_option === 'SELF-DELIVER';

  const getDriverId = useCallback(
    (existingAssignedRescue) => {
      if (isSelfDeliver) {
        return donation.pattern_driver_id ?? donation.user_id;
      }
      if (donation.is_partial && existingAssignedRescue) {
        return existingAssignedRescue.driver_id;
      }
      return user.id;
    },
    [
      donation.is_partial,
      donation.pattern_driver_id,
      donation.user_id,
      isSelfDeliver,
      user.id,
    ]
  );

  const getVehicleId = useCallback(
    (existingAssignedRescue) => {
      if (isSelfDeliver) {
        return donation.pattern_vehicle_id || null;
      }
      if (donation.is_partial && existingAssignedRescue) {
        return existingAssignedRescue.vehicle_id || null;
      }
      return null;
    },
    [donation.is_partial, donation.pattern_vehicle_id, isSelfDeliver]
  );

  const handleClaimClick = useCallback(
    async ({ partialClaims, replaceExistingPartialClaims = false } = {}) => {
      let existingPartialAssignedRescue = existingPartialRescue;
      if (donation.is_partial) {
        try {
          const existingRescue = existingPartialRescue
            ? { rescue: existingPartialRescue }
            : await checkForExistingRescue({
                donationId: donation.id,
                recipientTeamId: user.team_id,
              });
          if (existingRescue?.rescue && existingRescue?.rescue?.driver_id) {
            existingPartialAssignedRescue = existingRescue?.rescue;
          }
        } catch (err) {
          enqueueSnackbar(err.message, { variant: 'error' });
          return;
        }
      }

      if (
        (isSelfDeliver ||
          user.is_driver ||
          (donation.is_partial && existingPartialAssignedRescue)) &&
        !donation.is_food_run
      ) {
        setLoading(true);
        const driverId = getDriverId(existingPartialAssignedRescue);
        const vehicleId = getVehicleId(existingPartialAssignedRescue);

        backend.rescues
          .create({
            donationId: donation.id,
            drivers: [
              {
                user_id: driverId,
                team_id: isSelfDeliver ? donation.donor_team_id : user.team_id,
                vehicle_id: vehicleId,
              },
            ],
            vehicleId: vehicleId,
            user: { id: user.id, team_id: user.team_id },
            isPartialRescue: donation.is_partial,
            partialClaims,
            replaceExistingPartialClaims,
          })
          .then((rescueId) => {
            if (donation.is_partial) {
              history.push(
                PATH_DASHBOARD.viewPartialDonation(donationId, rescueId)
              );
            }
            onClaim();
          })
          .catch((err) => enqueueSnackbar(err.message, { variant: 'error' }))
          .finally(() => setLoading(false));
      } else {
        setPortionDetails({ partialClaims, replaceExistingPartialClaims });
        handleOpen();
      }
    },
    [
      checkForExistingRescue,
      donation,
      donationId,
      enqueueSnackbar,
      existingPartialRescue,
      getDriverId,
      getVehicleId,
      handleOpen,
      history,
      isSelfDeliver,
      onClaim,
      user,
    ]
  );
  const handleOpenPartialClaim = useCallback(async () => {
    try {
      const existingRescue = await checkForExistingRescue({
        donationId: donation.id,
        recipientTeamId: user.team_id,
      });
      setExistingPartialRescue(existingRescue?.rescue || null);
      setOpenDialogClaimPortion(true);
    } catch (err) {
      enqueueSnackbar(err.message, { variant: 'error' });
    }
  }, [checkForExistingRescue, donation.id, enqueueSnackbar, user.team_id]);
  const handleClosePartialClaim = useCallback(() => {
    setOpenDialogClaimPortion(false);
  }, []);
  return (
    <>
      <LoadingButton
        data-tour="donation-claim-button"
        data-tour-claim-opens-driver-modal={
          claimOpensDriverModal ? 'true' : undefined
        }
        loading={loading || isLoadingCheckForExistingRescue}
        startIcon={
          hasExistingPartialClaim ? <TuneRounded /> : <Inventory2Rounded />
        }
        variant="contained"
        sx={{ alignSelf: 'flex-start', fontWeight: 800 }}
        onClick={
          donation.is_partial ? handleOpenPartialClaim : handleClaimClick
        }
        disabled={disabled}
      >
        {hasExistingPartialClaim
          ? t('donations.adjust-claim', { defaultValue: 'Adjust claim' })
          : t('donations.claim-donation', { defaultValue: 'Claim donation' })}
      </LoadingButton>
      {donation?.is_partial ? (
        <DialogClaimPartOfPartialRescue
          onClaim={handleClaimClick}
          donation={donation}
          existingRescue={existingPartialRescue}
          open={openDialogClaimPortion}
          close={handleClosePartialClaim}
        />
      ) : null}

      {open && (
        <DriverSelectorModal
          canConvertToFoodRun={donation.rescue_status !== 'ASSIGNED'}
          donation={donation}
          open={open}
          onClose={handleClose}
          handleDriverAssign={handleDriverAssign}
        />
      )}
    </>
  );
}

function UnclaimDonation({ donation, onUnclaim }) {
  const { t } = useTranslation('translation');
  const { enqueueSnackbar } = useSnackbar();

  const [open, setOpen] = useState(false);
  const handleOpen = useCallback(() => setOpen(true), []);
  const handleClose = useCallback(() => setOpen(false), []);

  const { activeMembers } = useTeamMembers();

  const emitInvalidateIncomingRequests = useEventBus(
    'incoming-request',
    'invalidate'
  );

  const isPreAcceptance =
    donation?.rescue_status === 'WAITING_FOR_APPROVAL' ||
    donation?.rescue_status === 'AWAITING_DRIVER';

  return (
    <>
      <Tooltip
        title={
          isPreAcceptance
            ? t('donations.cancel-rescue-request-tooltip', {
                defaultValue: 'Decline this rescue request.',
              })
            : t('donations.cancel-rescue-tooltip', {
                defaultValue: 'Cancel this rescue claim.',
              })
        }
      >
        <span>
          <LoadingButton
            data-tour="donation-cancel-button"
            startIcon={<CloseRounded />}
            color="error"
            onClick={handleOpen}
            variant="outlined"
            sx={{ fontWeight: 800 }}
          >
            {t('donations.cancel')}
          </LoadingButton>
        </span>
      </Tooltip>

      <RescueCancelConfirmationModal
        open={open}
        title={
          isPreAcceptance
            ? t('donations.are-you-sure-you-want-to-cancel-rescue')
            : t('donations.confirm-cancel-rescue')
        }
        okLabel={t('donations.cancel-rescue-ok')}
        handleCancel={handleClose}
        donation={donation}
        teamMembers={activeMembers}
        onUnClaim={() => {
          enqueueSnackbar(t('donations.donation-unclaimed'), {
            variant: 'success',
          });
          onUnclaim();
          emitInvalidateIncomingRequests();
        }}
      />
    </>
  );
}

function DriverSelfUnassignButton({ donation, onUnassign }) {
  const { t } = useTranslation('translation');
  const { enqueueSnackbar } = useSnackbar();
  const [open, setOpen] = useState(false);
  const isRecurring = Boolean(donation?.is_recurring);
  // Only driver accounts on the rescue see this button, so on a food run it
  // removes just them (the server's self-unassign); other drivers stay.
  const isFoodRun = Boolean(donation?.is_food_run);
  const actionLabel = isFoodRun
    ? t('donations.remove-me-from-this-run')
    : t('donations.unassign-yourself', {
        defaultValue: 'Unassign yourself',
      });

  const handleClose = useCallback(() => setOpen(false), []);
  const handleOpen = useCallback(() => setOpen(true), []);

  const handleUnassign = useCallback(async () => {
    try {
      if (isFoodRun) {
        await donations.unassignSingleFoodInstance({
          rescueId: donation.rescue_id,
          parentId: donation.parent_id,
          donationId: donation.id,
        });
      } else {
        await rescues.cancel({
          donationId: donation.id,
          shouldUpdateCalendar: true,
          isPartialRescue: donation.is_partial,
          rescueId: donation.rescue_id,
          recipientTeamId: donation.recipient_team_id,
        });
      }

      enqueueSnackbar(
        isFoodRun
          ? t('donations.removed-from-this-run')
          : isRecurring
            ? t('donations.driver-unassigned-recurring', {
                defaultValue:
                  'You have been unassigned from this scheduled pickup.',
              })
            : t('donations.driver-unassigned', {
                defaultValue: 'You have been unassigned from this rescue.',
              }),
        { variant: 'success' }
      );
      handleClose();
      onUnassign();
    } catch (err) {
      enqueueSnackbar(
        err?.message ||
          t('donations.driver-unassign-error', {
            defaultValue:
              'Something went wrong while unassigning you from this rescue.',
          }),
        { variant: 'error' }
      );
    }
  }, [
    donation,
    enqueueSnackbar,
    handleClose,
    isFoodRun,
    isRecurring,
    onUnassign,
    t,
  ]);

  return (
    <>
      <Tooltip
        title={
          isRecurring
            ? t('donations.unassign-yourself-recurring-tooltip', {
                defaultValue:
                  'Remove yourself from this scheduled pickup only.',
              })
            : t('donations.unassign-yourself-tooltip', {
                defaultValue: 'Remove yourself as the assigned driver.',
              })
        }
      >
        <LoadingButton
          color="inherit"
          onClick={handleOpen}
          size="small"
          startIcon={<PersonRemoveRounded />}
          sx={{
            borderColor: 'divider',
            borderRadius: 1,
            color: 'text.primary',
            flexShrink: 0,
            fontWeight: 800,
            minHeight: 30,
            '&:hover': {
              borderColor: 'text.secondary',
              bgcolor: 'action.hover',
            },
          }}
          variant="outlined"
        >
          {actionLabel}
        </LoadingButton>
      </Tooltip>
      <ConfirmModal
        open={open}
        handleCancel={handleClose}
        onClick={handleUnassign}
        title={
          isFoodRun
            ? t('donations.confirm-remove-me-from-this-run')
            : isRecurring
              ? t('donations.confirm-unassign-yourself-recurring', {
                  defaultValue: 'Unassign yourself from this pickup only?',
                })
              : t('donations.confirm-unassign-yourself', {
                  defaultValue: 'Unassign yourself from this rescue?',
                })
        }
        confirmMessage={
          isFoodRun
            ? t('donations.confirm-remove-me-from-this-run-description')
            : isRecurring
              ? t('donations.confirm-unassign-yourself-recurring-description', {
                  defaultValue:
                    'This only removes you from this scheduled pickup. It stays with your organization for reassignment, and future recurring pickups are not changed.',
                })
              : t('donations.confirm-unassign-yourself-description', {
                  defaultValue:
                    'The rescue will stay with your organization so an admin or staff member can reassign it.',
                })
        }
        okLabel={actionLabel}
        cancelLabel={t('donations.keep-rescue', {
          defaultValue: 'Keep rescue',
        })}
        okButtonColor="primary"
        okButtonVariant="contained"
      />
    </>
  );
}

function StartRescue({ donation, donationId, rescueId, onStart }) {
  const { t } = useTranslation('translation');
  const { user } = useAuth();
  const { enqueueSnackbar } = useSnackbar();

  const [loading, setLoading] = useState(false);

  const handleStart = useCallback(() => {
    setLoading(true);

    rescues
      .start({
        donationId,
        rescueId,
        vehicleId: donation?.vehicle_mapped?.id,
        isPartialRescue: donation.is_partial,
      })
      .then(() => {
        enqueueSnackbar(t('donations.rescue-started'), {
          variant: 'success',
        });
        onStart();
      })
      .catch((err) => {
        enqueueSnackbar(err.message, {
          variant: 'error',
        });
        console.error(err);
      })
      .finally(() => setLoading(false));
  }, [donationId, rescueId, onStart, enqueueSnackbar]);

  const currentTime = moment().tz(donation.donor_timezone_id);
  const startDateMinus24Hours = moment(donation.start_date)
    .tz(donation.donor_timezone_id)
    .subtract(24, 'hours');
  const endDatePlus24Hours = moment(donation.end_date)
    .tz(donation.donor_timezone_id)
    .add(24, 'hours');

  const available = currentTime.isBetween(
    startDateMinus24Hours,
    endDatePlus24Hours
  );

  const beforeStart = currentTime.isBefore(startDateMinus24Hours);
  const afterEnd = currentTime.isAfter(endDatePlus24Hours);

  let helpMessage;
  if (!available && beforeStart) {
    helpMessage = t('donations.you-canot-begin-this-rescue-before-start-time');
  } else if (!available && afterEnd) {
    helpMessage = t('donations.you-canot-begin-this-rescue-after-end-time');
  } else if (!user.can_rescue) {
    helpMessage = t('donations.you-are-not-allowed-to-rescue');
  }

  return (
    <Tooltip title={helpMessage}>
      {/* The <span /> is needed so that the <Tooltip /> displays even then <LoadingButton /> is disabled. */}
      <span>
        <LoadingButton
          loading={loading}
          startIcon={<PlayArrowRounded />}
          color="primary"
          variant="contained"
          onClick={handleStart}
          sx={{ fontWeight: 800 }}
          disabled={!available || !user.can_rescue}
        >
          {t('donations.start-rescue')}
        </LoadingButton>
      </span>
    </Tooltip>
  );
}

export function ChatWithParticipants({ donation }) {
  const { t } = useTranslation('translation');
  const { enqueueSnackbar } = useSnackbar();
  const [loadingChat, setLoadingChat] = useState(false);
  const {
    setActiveConversationId,
    setChatOpen,
    setDrawerOpen,
    setPendingChat,
    conversations,
  } = useChatClient();
  const handleJoinConversation = useCallback(async () => {
    setLoadingChat(true);

    try {
      // Find an existing donation/rescue conversation first.
      // If none exists, open chat in pending mode and create it on first send.
      const conversationId = await chat.getDonationConversation(
        donation?.id,
        donation?.rescue_id
      );

      setDrawerOpen(true);
      setChatOpen(true);

      if (conversationId) {
        setActiveConversationId(conversationId);
        setPendingChat(null);
      } else {
        // No conversation yet — open chat in pending mode.
        // Conversation will be created on first message send.
        setActiveConversationId(null);
        setPendingChat({
          type: 'donation',
          id: donation?.id,
          title: donation?.title,
          rescueId: donation?.rescue_id,
        });
      }
    } catch (err) {
      console.error(err);
      Sentry.captureException(err);
      enqueueSnackbar(t('form.errors.generic'), {
        variant: 'error',
      });
    }

    setLoadingChat(false);
  }, [
    donation?.id,
    donation?.rescue_id,
    donation?.title,
    enqueueSnackbar,
    setActiveConversationId,
    setChatOpen,
    setDrawerOpen,
    setPendingChat,
    t,
  ]);

  const unreadMessages = useMemo(() => {
    const conversation = conversations[donation?.conversation_id];
    if (!conversation) {
      return 0;
    }

    const last = conversation.lastMessage?.index ?? 0;
    const read = conversation.lastReadMessageIndex ?? 0;
    return last - read;
  }, [conversations, donation?.conversation_id]);

  return (
    <Tooltip title={t('donations.chat-with-participants')}>
      <MBadge
        badgeContent={unreadMessages || null}
        color="error"
        invisible={!unreadMessages}
        sx={{ display: 'inline-flex', '& .MuiBadge-badge': { top: 4 } }}
      >
        <Button
          data-tour="donation-chat-button"
          disabled={loadingChat}
          onClick={handleJoinConversation}
          size="small"
          sx={{
            borderColor: 'divider',
            color: 'text.primary',
            minHeight: 32,
            '&:hover': {
              bgcolor: 'action.hover',
              borderColor: 'text.secondary',
            },
          }}
          variant="outlined"
          startIcon={<SmsOutlined />}
        >
          {t('chat.chat', { defaultValue: 'Chat' })}
        </Button>
      </MBadge>
    </Tooltip>
  );
}

const EditConfirmModal = ({
  open,
  handleClose,
  handleEditDonationSingleInstance,
  handleEditFutureDonations,
}) => {
  const { t } = useTranslation('translation');
  return (
    <Dialog maxWidth="md" fullWidth open={open} onClose={handleClose}>
      <>
        <Box
          marginBottom="15px"
          width="100%"
          display="flex"
          justifyContent="space-between"
          alignItems="center"
        >
          <DialogTitle>{t('donations.edit-donation')}</DialogTitle>
        </Box>
        <DialogContent>
          <Typography>
            {t('donations.edit-description', {
              date: formatDisplayDate(moment(), 'date'),
            })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>{t('donations.close')}</Button>
          <Button
            variant="contained"
            onClick={handleEditDonationSingleInstance}
          >
            {t('donations.edit-this-donation')}
          </Button>
          <Button variant="contained" onClick={handleEditFutureDonations}>
            {t('donations.edit-all-future-donations')}
          </Button>
        </DialogActions>
      </>
    </Dialog>
  );
};

const UploadPhotoDonation = ({
  open,
  close,
  donationId,
  isRecurring,
  invalidate,
  patternId,
}) => {
  const { t } = useTranslation('translation');
  const [files, setFiles] = useState([]);
  const [isAdditional, setIsAdditional] = useState(false);
  const [loading, setLoading] = useState(false);
  const handleUploadPhotos = async () => {
    setLoading(true);
    try {
      const s3Folder = isRecurring
        ? `recurring-pattern/${patternId}`
        : `donation/${donationId}`;

      const uploadedImages = await uploadImages({
        images: files,
        folder: s3Folder,
        source: 'Donation details',
      });

      backend.donations
        .createDonationImages({
          donationId,
          images: uploadedImages,
          isRecurring,
          patternId,
          isAdditional,
        })
        .then(invalidate);
      close();
      setFiles([]);
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
  };
  return (
    <Dialog maxWidth="xs" fullWidth open={open} onClose={close}>
      <>
        <Box
          marginBottom="15px"
          width="100%"
          display="flex"
          justifyContent="space-between"
          alignItems="center"
        >
          <DialogTitle>{t('donations.upload-photos')}</DialogTitle>
        </Box>
        <DialogContent>
          <Box mb={2}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={isAdditional}
                  onChange={(e) => setIsAdditional(e.target.checked)}
                />
              }
              label={t('donations.additional-information')}
            />
          </Box>
          <UploadImagesComponent
            label={t('common.images')}
            displayLabel
            value={files}
            width="100%"
            onChange={(files) => setFiles(files)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={close}>{t('donations.close')}</Button>
          <LoadingButton
            loading={loading}
            onClick={handleUploadPhotos}
            disabled={!files.length || loading}
            variant="contained"
          >
            {t('donations.upload-photos')}
          </LoadingButton>
        </DialogActions>
      </>
    </Dialog>
  );
};

const RepostConfirmModal = ({ open, handleClose, handleRepost }) => {
  const { t } = useTranslation('translation');
  return (
    <Dialog maxWidth="md" fullWidth open={open} onClose={handleClose}>
      <>
        <Box
          marginBottom="15px"
          width="100%"
          display="flex"
          justifyContent="space-between"
          alignItems="center"
        >
          <DialogTitle>{t('donations.repost-this-donation')}</DialogTitle>
        </Box>
        <DialogContent>
          <Typography>
            {t('donations.repost-this-donation-description')}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>{t('donations.close')}</Button>
          <Button variant="contained" onClick={handleRepost}>
            {t('donations.repost-this-donation')}
          </Button>
        </DialogActions>
      </>
    </Dialog>
  );
};

export const CardEmptyState = ({ text, ...props }) => {
  return (
    <Box
      {...props}
      sx={{
        border: (t) => `1px solid ${t.palette.divider}`,
        borderRadius: '7px',
        py: 5,
        px: 3,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderStyle: 'dashed',
        ...props?.sx,
      }}
    >
      <Typography textAlign="center" variant="body2" color={labelColor}>
        {text}
      </Typography>
    </Box>
  );
};

const ListCardNote = ({ notes, totalDocs, handleEdit, ...props }) => {
  const { user } = useAuth();
  if (totalDocs == 0) {
    return <></>;
  }

  return notes
    ?.sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
    ?.map((note, idx, arr) => {
      const isLast = idx === arr.length - 1;
      return (
        <React.Fragment key={note?.id}>
          <Box
            {...props}
            sx={{
              ...props?.sx,
              pb: isLast ? 0 : 2,
            }}
          >
            <Stack direction="row" justifyContent="space-between" spacing={1}>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="caption" color="text.secondary">
                  {formatDisplayDate(moment(note?.created_at), 'date')} ·{' '}
                  {moment.utc(note?.created_at).format('HH:mm')}
                </Typography>
                <Typography variant="body2" fontWeight={700}>
                  {note?.user_name}
                </Typography>
              </Box>
              <Box sx={{ flexShrink: 0 }}>
                {note?.owner_user_id === user.id &&
                  note?.owner_team_id === user.team_id && (
                    <IconButton
                      onClick={() => handleEdit(note?.id, note?.notes)}
                      color="primary"
                    >
                      <Edit />
                    </IconButton>
                  )}
              </Box>
            </Stack>

            <Typography
              sx={{
                mt: 1,
                whiteSpace: 'pre-wrap',
                overflowWrap: 'break-word',
              }}
              variant="body2"
            >
              {note?.notes}
            </Typography>
          </Box>
          {!isLast && <Divider sx={{ borderStyle: 'dashed !important' }} />}
        </React.Fragment>
      );
    });
};

export function DonationNotes({
  donationId,
  donation,
  user,
  hideWhenEmpty = false,
  sx,
}) {
  const { t } = useTranslation('translation');
  const [id, setId] = useState(null);
  const [value, setValue] = useState('');
  const [open, setOpen] = useState(false);
  const handleCancel = () => {
    setId('');
    setEdit(false);
    setOpen(false);
  };
  const [isEdit, setEdit] = useState(false);
  const handleEdit = (id, note) => {
    setEdit(true);
    setId(id);
    setOpen(true);
    setValue(note);
  };
  const { notes, handleCreateNotes, isLoading, handleUpdateNotes } = useNotes({
    donationId,
    successCb: () => {
      setId(null);
      setEdit(false);
      setValue('');
      setOpen(false);
    },
  });
  const handleOk = () => {
    if (isEdit) {
      handleUpdateNotes({ id, isRescue: false, value });
    } else {
      handleCreateNotes({ isRescue: false, value });
    }
  };

  if (
    !isLoading &&
    hideWhenEmpty &&
    (!Array.isArray(notes) || notes.length === 0)
  ) {
    return null;
  }

  return (
    <>
      {isLoading ? (
        <Skeleton
          sx={{
            mt: sx?.mt ?? 3,
          }}
          variant="rounded"
          width="100%"
          height={252}
        />
      ) : (
        <Card sx={{ p: 3, mt: 3, ...sx }}>
          <Stack direction="row" justifyContent="space-between" spacing={1}>
            <TitleInfo variant="h5">{t('donations.notes')}</TitleInfo>
            {(user?.team_id === donation?.donor_team_id ||
              user?.team_id === donation?.food_mover_team_id) && (
              <Button size="small" onClick={() => setOpen(true)}>
                <Add />
                {t('donations.add-note')}
              </Button>
            )}
          </Stack>
          <Typography variant="body2" color={labelColor}>
            {t('donations.notes-count', { count: notes?.length || 0 })}
          </Typography>
          <ListCardNote
            handleEdit={handleEdit}
            notes={notes}
            totalDocs={notes ? notes?.length : 0}
            sx={{ mt: 3 }}
          />
        </Card>
      )}
      <Dialog open={open} onClose={handleCancel} fullWidth>
        <DialogTitle>
          {isEdit ? t('donations.edit-note') : t('donations.add-note')}
        </DialogTitle>
        <DialogContent>
          <TextField
            label={t('donations.my-notes')}
            multiline
            rows={4}
            fullWidth
            margin="normal"
            value={value}
            onChange={(e) => setValue(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancel}>{t('donations.cancel')}</Button>
          <LoadingButton
            variant="contained"
            color="primary"
            onClick={handleOk}
            loading={isLoading}
          >
            {t('donations.submit')}
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </>
  );
}

export function InfoContainer({
  children,
  loading = false,
  skeletonHeight = 56,
  ...props
}) {
  return loading ? (
    <Skeleton
      variant="rounded"
      width={'100%'}
      height={skeletonHeight}
      sx={{
        mt: 2,
      }}
    />
  ) : (
    <Box
      bgcolor="rgba(54, 179, 126, 0.08)"
      p={2}
      borderRadius="7px"
      mt={2}
      {...props}
    >
      {children}
    </Box>
  );
}

// ----------------------------------------------------------------------

function useNotes({ donationId, successCb = _noop }) {
  const { t } = useTranslation('translation');
  const [notes, setNotes] = useState('');
  const [isLoading, setLoading] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    fetchData();
  }, [donationId, setNotes]);

  const fetchData = useCallback(() => {
    if (donationId) {
      backend.notes
        .getAllNotesOfItem(donationId)
        .then(setNotes)
        .catch((err) => {
          setNotes([]);
          if (err?.status !== 403) console.error(err);
        });
    }
  }, [donationId, setNotes]);

  const handleUpdateNotes = useCallback(
    ({ isRescue, id, value }) => {
      setLoading(true);
      backend.notes
        .update({ itemId: donationId, isRescue, value, id })
        .then(() => {
          successCb();
          fetchData();
        })
        .catch(() =>
          enqueueSnackbar(t('form.errors.generic'), {
            variant: 'error',
          })
        )
        .finally(() => setLoading(false));
    },
    [donationId, setNotes]
  );

  const handleCreateNotes = useCallback(
    ({ isRescue, value }) => {
      setLoading(true);
      backend.notes
        .create({ itemId: donationId, isRescue, value })
        .then(() => {
          successCb();
          fetchData();
        })
        .catch(() =>
          enqueueSnackbar(t('form.errors.generic'), {
            variant: 'error',
          })
        )
        .finally(() => setLoading(false));
    },
    [donationId, setNotes, setLoading]
  );

  return { notes, handleUpdateNotes, handleCreateNotes, isLoading };
}

const groupFoodWeights = (donation = {}) => {
  return {
    baked: donation?.baked_weight || null,
    baked_unit: donation?.baked_weight_unit,
    dairy: donation?.dairy_weight || null,
    dairy_unit: donation?.dairy_weight_unit,
    dairy_pickup_temp: donation?.dairy_pickup_temp,
    dairy_pickup_temp_unit: donation?.dairy_pickup_temp_unit,
    dairy_dropoff_temp: donation?.dairy_dropoff_temp,
    dairy_dropoff_temp_unit: donation?.dairy_dropoff_temp_unit,
    meat: donation?.meat_weight || null,
    meat_unit: donation?.meat_weight_unit,
    meat_pickup_temp: donation?.meat_pickup_temp,
    meat_pickup_temp_unit: donation?.meat_pickup_temp_unit,
    meat_dropoff_temp: donation?.meat_dropoff_temp,
    meat_dropoff_temp_unit: donation?.meat_dropoff_temp_unit,
    produce: donation?.produce_weight || null,
    produce_unit: donation?.produce_weight_unit,
    produce_pickup_temp: donation?.produce_pickup_temp,
    produce_pickup_temp_unit: donation?.produce_pickup_temp_unit,
    produce_dropoff_temp: donation?.produce_dropoff_temp,
    produce_dropoff_temp_unit: donation?.produce_dropoff_temp_unit,
    prepared: donation?.prepared_weight || null,
    prepared_unit: donation?.prepared_weight_unit,
    prepared_pickup_temp: donation?.prepared_pickup_temp,
    prepared_pickup_temp_unit: donation?.prepared_pickup_temp_unit,
    prepared_dropoff_temp: donation?.prepared_dropoff_temp,
    prepared_dropoff_temp_unit: donation?.prepared_dropoff_temp_unit,
    non_food: donation?.non_food_weight,
    non_food_unit: donation?.non_food_weight_unit,
    frozen: donation?.frozen_weight || null,
    frozen_unit: donation?.frozen_weight_unit,
    compost: donation?.compost_weight,
    compost_unit: donation?.compost_weight_unit,
    unusable: donation?.unusable_weight || null,
    unusable_unit: donation?.unusable_weight_unit,
    beverages: donation?.beverages_weight || null,
    beverages_unit: donation?.beverages_weight_unit,
    non_perishable: donation?.non_perishable_weight || null,
    non_perishable_unit: donation?.non_perishable_weight_unit,
    animal_feed: donation?.animal_feed_weight || null,
    animal_feed_unit: donation?.animal_feed_weight_unit,
  };
};

const aggregatePartialRescueWeights = (allRescues = []) => {
  const result = {
    baked: null,
    baked_unit: 'lbs',
    dairy: null,
    dairy_unit: 'lbs',
    meat: null,
    meat_unit: 'lbs',
    produce: null,
    produce_unit: 'lbs',
    prepared: null,
    prepared_unit: 'lbs',
    non_food: null,
    non_food_unit: 'lbs',
    frozen: null,
    frozen_unit: 'lbs',
    compost: null,
    compost_unit: 'lbs',
    unusable: null,
    unusable_unit: 'lbs',
    beverages: null,
    beverages_unit: 'lbs',
    non_perishable: null,
    non_perishable_unit: 'lbs',
    animal_feed: null,
    animal_feed_unit: 'lbs',
  };

  if (!allRescues.length) {
    return [];
  }

  const foodMappings = {
    baked: 'baked_weight',
    dairy: 'dairy_weight',
    meat: 'meat_weight',
    produce: 'produce_weight',
    prepared: 'prepared_weight',
    non_food: 'non_food_weight',
    frozen: 'frozen_weight',
    compost: 'compost_weight',
    unusable: 'unusable_weight',
    beverages: 'beverages_weight',
    non_perishable: 'non_perishable_weight',
    animal_feed: 'animal_feed_weight',
  };

  allRescues.forEach((rescue) => {
    Object.entries(foodMappings).forEach(([resultField, rescueField]) => {
      const weight = rescue?.[rescueField];

      if (weight !== null) {
        result[resultField] = (result[resultField] || 0) + weight;
      }
    });
  });

  Object.keys(foodMappings).forEach((field) => {
    if (isNaN(result[field])) {
      result[field] = null;
    }
  });

  return groupByUnit(result);
};

function useFoodWeights(donation) {
  return useMemo(() => donation && groupFoodWeights(donation), [donation]);
}

export const TitleInfo = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.primary,
  fontSize: '1rem',
  fontWeight: 800,
  lineHeight: 1.25,
}));

const detailCardSx = {
  border: (theme) => `1px solid ${theme.palette.divider}`,
  boxShadow: '0px 8px 24px -12px rgba(99, 115, 129, 0.35)',
};

const normalizeFoodTypeKey = (foodType = '') =>
  String(foodType).toLowerCase().replaceAll('_', '-');

const foodTypeIconMap = {
  'animal-feed': FoodTypesIcons.AnimalFeedIcon,
  baked: FoodTypesIcons.BakeryIcon,
  beverages: FoodTypesIcons.BeveragesIcon,
  compost: FoodTypesIcons.CompostIcon,
  dairy: FoodTypesIcons.DairyIcon,
  frozen: FoodTypesIcons.FrozenIcon,
  meat: FoodTypesIcons.MeatIcon,
  'non-food': FoodTypesIcons.NonFoodIcon,
  'non-perishable': FoodTypesIcons.NonPerishableIcon,
  prepared: FoodTypesIcons.PreparedIcon,
  produce: FoodTypesIcons.ProduceIcon,
};

const getFoodTypeTitle = (t, foodType) => {
  const normalized = normalizeFoodTypeKey(foodType);
  try {
    return getFoodTypeLabel(t, normalized);
  } catch {
    return capitalize(normalized.replaceAll('-', ' '));
  }
};

const formatLbs = (valueLbs, unit = 'lbs') => {
  const amount = new Weight(Number(valueLbs) || 0, 'lbs').asRounded(
    unit || 'lbs'
  );
  return `${amount} ${unit || 'lbs'}`;
};

const FoodTypeIconBadge = ({ foodType }) => {
  const Icon = foodTypeIconMap[normalizeFoodTypeKey(foodType)];
  if (!Icon) return null;

  return (
    <Box
      sx={{
        alignItems: 'center',
        bgcolor: 'grey.100',
        borderRadius: 1,
        color: 'text.secondary',
        display: 'flex',
        flexShrink: 0,
        height: 44,
        justifyContent: 'center',
        width: 44,
      }}
    >
      <Icon sx={{ fontSize: 28 }} />
    </Box>
  );
};

const DetailCard = ({ title, action, children, sx }) => (
  <Card sx={{ p: 2.5, ...detailCardSx, ...sx }}>
    {(title || action) && (
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        spacing={2}
        mb={2}
      >
        {title ? (
          <Typography
            variant="subtitle1"
            sx={{ color: 'text.primary', fontWeight: 800, lineHeight: 1.25 }}
          >
            {title}
          </Typography>
        ) : (
          <Box />
        )}
        {action}
      </Stack>
    )}
    {children}
  </Card>
);

const DetailLine = ({ label, value, helper, icon }) => (
  <Stack direction="row" spacing={1.25} alignItems="flex-start">
    {icon ? (
      <Box sx={{ color: 'text.secondary', display: 'flex', pt: 0.2 }}>
        {icon}
      </Box>
    ) : null}
    <Box sx={{ minWidth: 0 }}>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body2" fontWeight={700} sx={{ mt: 0.25 }}>
        {value || '-'}
      </Typography>
      {helper ? (
        <Typography variant="caption" color="text.secondary">
          {helper}
        </Typography>
      ) : null}
    </Box>
  </Stack>
);

const getStatusTone = (status) => {
  switch (status) {
    case 'COMPLETED':
      return { bgcolor: 'success.lighter', color: 'success.darker' };
    case 'CANCELLED':
    case 'INCOMPLETE':
      return { bgcolor: 'error.lighter', color: 'error.darker' };
    case 'WAITING_FOR_APPROVAL':
    case 'WAITING_FOR_REASSIGN':
      return { bgcolor: 'warning.lighter', color: 'warning.darker' };
    case 'PICKUP_IN_PROGRESS':
    case 'DELIVERY_IN_PROGRESS':
    case 'STARTED':
    case 'ASSIGNED':
      return { bgcolor: 'grey.100', color: 'text.primary' };
    case 'AVAILABLE':
    case 'OPEN':
    default:
      return { bgcolor: 'grey.100', color: 'text.secondary' };
  }
};

const StatusBadge = ({ status, label }) => {
  const tone = getStatusTone(status);

  return (
    <Box
      sx={{
        alignItems: 'center',
        bgcolor: tone.bgcolor,
        borderRadius: 1,
        color: tone.color,
        display: 'inline-flex',
        gap: 0.75,
        lineHeight: 1,
        px: 1,
        py: 0.75,
        whiteSpace: 'nowrap',
      }}
    >
      <Box
        sx={{
          bgcolor: 'currentColor',
          borderRadius: '50%',
          height: 6,
          width: 6,
        }}
      />
      <Typography
        component="span"
        variant="caption"
        sx={{ fontWeight: 800, textTransform: 'capitalize' }}
      >
        {label}
      </Typography>
    </Box>
  );
};

const getGoogleMapsSearchUrl = (location) => {
  const query =
    location?.formatted_address ||
    (Number.isFinite(location?.lat) && Number.isFinite(location?.lng)
      ? `${location.lat},${location.lng}`
      : '');

  if (!query) return null;

  const params = new URLSearchParams({
    api: '1',
    query,
  });

  if (location?.place_id) {
    params.set('query_place_id', location.place_id);
  }

  return `https://www.google.com/maps/search/?${params.toString()}`;
};

const AddressLink = ({ location, children }) => {
  const href = getGoogleMapsSearchUrl(location);

  if (!href) {
    return children;
  }

  return (
    <Link
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      underline="hover"
      color="inherit"
      sx={{ fontWeight: 'inherit' }}
    >
      {children}
    </Link>
  );
};

const formatDonationWindow = ({ donation, timeZoneId }) => {
  const start = moment.utc(donation?.start_date).tz(timeZoneId);
  const end = moment.utc(donation?.end_date).tz(timeZoneId);

  if (!start.isValid()) {
    return { date: '-', time: null };
  }

  if (donation?.delivery_option === 'STAFF-MEALS' || !end.isValid()) {
    return {
      date: formatDisplayDate(start, 'weekdayDateLong'),
      time: formatDisplayDate(start, 'time', { zone: true }),
    };
  }

  if (start.isSame(end, 'day')) {
    return {
      date: formatDisplayDate(start, 'weekdayDateLong'),
      time: `${formatDisplayDate(start, 'time')} - ${formatDisplayDate(end, 'time', { zone: true })}`,
    };
  }

  return {
    date: `${formatDisplayDate(start, 'weekdayMonthDay', { time: true })} -`,
    time: formatDisplayDate(end, 'weekdayMonthDay', { time: true, zone: true }),
  };
};

const recurringDayNames = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
];

const formatRecurringDays = (days, t) => {
  return parseJsonArray(days)
    .map((day) => {
      const dayIndex = Number(day);
      const dayName = recurringDayNames[dayIndex];
      if (!dayName) return day;
      return t(`donations.${dayName}`, { defaultValue: dayName });
    })
    .filter(Boolean)
    .join(', ');
};

const formatMonthlyRecurringDetail = ({ donation, t }) => {
  if (donation?.is_last_day_of_the_month) {
    return t('donations.monthly-on-last-day', {
      defaultValue: 'Last day of each month',
    });
  }

  if (donation?.no_of_the_week_in_month && donation?.day_of_the_week_in_month) {
    const ordinal = t(`donations.${donation.no_of_the_week_in_month}`, {
      defaultValue: donation.no_of_the_week_in_month,
    });
    const dayName = t(`donations.${donation.day_of_the_week_in_month}`, {
      defaultValue: donation.day_of_the_week_in_month,
    });
    return `${ordinal} ${dayName}`;
  }

  if (donation?.day_of_the_month) {
    return t('donations.monthly-on-day', {
      day: donation.day_of_the_month,
      defaultValue: `Day ${donation.day_of_the_month} of each month`,
    });
  }

  return null;
};

const getRecurringScheduleInfo = ({ donation, getRecurringType, t }) => {
  if (!donation?.is_recurring) return null;

  const recurringType =
    donation?.recurring_interval || donation?.recurring_type;
  const frequency =
    recurringType === 'UNLIMITED'
      ? t('donations.indefinite', { defaultValue: 'Indefinite' })
      : getRecurringType(recurringType) ||
        t('donations.recurring', { defaultValue: 'Recurring' });
  const dayLabels = formatRecurringDays(donation?.days_of_the_week, t);
  const detail =
    dayLabels && ['WEEKLY', 'BI-WEEKLY'].includes(recurringType)
      ? t('donations.recurring-on-days', {
          frequency,
          days: dayLabels,
          defaultValue: `${frequency} on ${dayLabels}`,
        })
      : recurringType === 'MONTHLY'
        ? formatMonthlyRecurringDetail({ donation, t }) || dayLabels
        : dayLabels;

  return {
    frequency,
    detail,
    isCancelled: Boolean(donation?.is_pattern_cancelled),
  };
};

const addLedgerAmount = (
  map,
  foodType,
  field,
  valueLbs,
  unit = 'lbs',
  options = {}
) => {
  const key = normalizeFoodTypeKey(foodType);
  if (!key || key === 'food') return;

  if (!map.has(key)) {
    map.set(key, {
      foodType: key,
      unit,
      originalLbs: 0,
      availableLbs: 0,
      claimedLbs: 0,
      currentClaimLbs: 0,
      hasSelectedFoodType: false,
    });
  }

  const row = map.get(key);
  row.unit = row.unit || unit;
  row[field] += Number(valueLbs) || 0;
  row.hasSelectedFoodType =
    row.hasSelectedFoodType || Boolean(options.hasSelectedFoodType);
};

const buildFoodLedgerRows = ({
  donation,
  parsedFoodWeightObject,
  allRescues,
  defaultUnit = 'lbs',
}) => {
  const rows = new Map();

  parseJsonArray(parsedFoodWeightObject).forEach((item) => {
    addLedgerAmount(
      rows,
      item.food_type,
      'originalLbs',
      item.estimated_weight,
      item.estimated_weight_unit || defaultUnit,
      { hasSelectedFoodType: true }
    );
  });

  const unassignedFromAllRescues = allRescues.flatMap((rescue) =>
    parseJsonArray(rescue.unassigned_food_types)
  );
  const unassignedFoodTypes = unassignedFromAllRescues.length
    ? unassignedFromAllRescues
    : parseJsonArray(donation?.unassigned_food_types);

  unassignedFoodTypes.forEach((item) => {
    addLedgerAmount(
      rows,
      item.food_type,
      'availableLbs',
      Number(item.partial_weight_reserved) || 0,
      item.weight_unit || defaultUnit
    );
  });

  allRescues.forEach((rescue) => {
    parseJsonArray(rescue.partial_food_types).forEach((item) => {
      addLedgerAmount(
        rows,
        item.food_type,
        'claimedLbs',
        Number(item.actual_weight ?? item.partial_weight_reserved) || 0,
        item.weight_unit || defaultUnit
      );
    });
  });

  parseJsonArray(donation?.partial_food_types).forEach((item) => {
    addLedgerAmount(
      rows,
      item.food_type,
      'currentClaimLbs',
      Number(item.actual_weight ?? item.partial_weight_reserved) || 0,
      item.weight_unit || defaultUnit
    );
  });

  return Array.from(rows.values())
    .map((row) => ({
      ...row,
      originalLbs:
        row.originalLbs ||
        row.availableLbs + row.claimedLbs ||
        row.currentClaimLbs,
    }))
    .filter(
      (row) =>
        row.originalLbs > 0 ||
        row.availableLbs > 0 ||
        row.claimedLbs > 0 ||
        row.currentClaimLbs > 0 ||
        row.hasSelectedFoodType
    )
    .sort((a, b) => a.foodType.localeCompare(b.foodType));
};

const FoodAvailabilityCard = ({
  rows,
  isPartial,
  defaultUnit,
  isSingleRescueView,
  isDonorTeam,
  action,
  t,
}) => {
  const claimLabel = t(
    isDonorTeam ? 'donations.this-claim' : 'donations.your-claim'
  );
  if (!rows.length) {
    return (
      <DetailCard
        title={t('donations.donation-amounts')}
        action={action}
        sx={{ py: 2 }}
      >
        <Typography variant="body2" color="text.secondary">
          {t('donations.no-food-types-selected')}
        </Typography>
      </DetailCard>
    );
  }

  const totalOriginalLbs = rows.reduce((sum, row) => sum + row.originalLbs, 0);
  const totalAvailableLbs = rows.reduce(
    (sum, row) => sum + row.availableLbs,
    0
  );
  const totalClaimedLbs = rows.reduce((sum, row) => sum + row.claimedLbs, 0);
  const totalCurrentClaimLbs = rows.reduce(
    (sum, row) => sum + row.currentClaimLbs,
    0
  );
  const showSummaryTotals = rows.length > 1;

  return (
    <DetailCard title={t('donations.donation-amounts')} action={action}>
      {showSummaryTotals ? (
        <Box
          sx={{
            display: 'grid',
            gap: 1,
            gridTemplateColumns: {
              xs: 'repeat(2, minmax(0, 1fr))',
              sm: isSingleRescueView
                ? 'repeat(4, minmax(0, 1fr))'
                : 'repeat(3, minmax(0, 1fr))',
            },
            mb: 2,
          }}
        >
          {[
            ...(isPartial
              ? [
                  [t('donations.original-total'), totalOriginalLbs],
                  [t('donations.available-to-claim'), totalAvailableLbs],
                  [t('donations.claimed-or-reserved'), totalClaimedLbs],
                ]
              : [[t('donations.total-weight'), totalOriginalLbs]]),
            ...(isSingleRescueView ? [[claimLabel, totalCurrentClaimLbs]] : []),
          ].map(([label, value], idx) => (
            <Box
              key={`${label}-${idx}`}
              sx={{
                bgcolor:
                  idx === 1 && isPartial ? 'primary.lighter' : 'grey.100',
                borderRadius: 1,
                px: 1.5,
                py: 1.25,
              }}
            >
              <Typography variant="caption" color="text.secondary">
                {label}
              </Typography>
              <Typography variant="subtitle1" fontWeight={800}>
                {formatLbs(value, defaultUnit)}
              </Typography>
            </Box>
          ))}
        </Box>
      ) : null}

      {!isPartial ? (
        <Box
          sx={{
            display: 'grid',
            gap: 1.25,
            gridTemplateColumns: {
              xs: '1fr',
              md: '1fr',
            },
          }}
        >
          {rows.map((row) => {
            const unit = row.unit || defaultUnit;

            return (
              <Box
                key={row.foodType}
                sx={{
                  alignItems: 'center',
                  bgcolor: 'background.paper',
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  display: 'grid',
                  gap: { xs: 1.25, sm: 2 },
                  gridTemplateColumns: {
                    xs: '1fr',
                    sm: 'minmax(0, 1fr) auto',
                  },
                  px: 1.5,
                  py: 1.25,
                }}
              >
                <Stack
                  direction="row"
                  spacing={1.25}
                  alignItems="center"
                  sx={{ minWidth: 0 }}
                >
                  <FoodTypeIconBadge foodType={row.foodType} />
                  <Typography variant="subtitle2">
                    {getFoodTypeTitle(t, row.foodType)}
                  </Typography>
                </Stack>

                <Box
                  sx={{
                    bgcolor: 'grey.100',
                    borderRadius: 1,
                    justifySelf: { xs: 'stretch', sm: 'end' },
                    minWidth: { sm: 128 },
                    px: 1.25,
                    py: 1,
                    textAlign: { xs: 'left', sm: 'right' },
                  }}
                >
                  <Typography variant="caption" color="text.secondary">
                    {t('donations.amount')}
                  </Typography>
                  <Typography variant="subtitle2" fontWeight={850}>
                    {formatLbs(row.originalLbs, unit)}
                  </Typography>
                </Box>
              </Box>
            );
          })}
        </Box>
      ) : (
        <Stack divider={<Divider />} spacing={0}>
          {rows.map((row) => {
            const availablePercent = row.originalLbs
              ? Math.min((row.availableLbs / row.originalLbs) * 100, 100)
              : 0;
            const claimedPercent = row.originalLbs
              ? Math.min((row.claimedLbs / row.originalLbs) * 100, 100)
              : 0;
            const unit = row.unit || defaultUnit;

            return (
              <Box key={row.foodType} sx={{ py: 1.5 }}>
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={1.5}
                  alignItems={{ xs: 'stretch', sm: 'center' }}
                >
                  <Stack
                    direction="row"
                    spacing={1.25}
                    alignItems="center"
                    sx={{ flex: 1, minWidth: 0 }}
                  >
                    <FoodTypeIconBadge foodType={row.foodType} />
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="subtitle2">
                        {getFoodTypeTitle(t, row.foodType)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {formatLbs(row.originalLbs, unit)}{' '}
                        {t('donations.original').toLowerCase()}
                      </Typography>
                    </Box>
                  </Stack>

                  <Stack
                    direction="row"
                    spacing={1.5}
                    justifyContent={{ xs: 'space-between', sm: 'flex-end' }}
                    sx={{ flexWrap: 'wrap' }}
                  >
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        {t('donations.available-amount')}
                      </Typography>
                      <Typography variant="body2" fontWeight={800}>
                        {formatLbs(row.availableLbs, unit)}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        {t('donations.reserved-amount')}
                      </Typography>
                      <Typography variant="body2" fontWeight={800}>
                        {formatLbs(row.claimedLbs, unit)}
                      </Typography>
                    </Box>
                    {isSingleRescueView && row.currentClaimLbs > 0 ? (
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          {claimLabel}
                        </Typography>
                        <Typography variant="body2" fontWeight={800}>
                          {formatLbs(row.currentClaimLbs, unit)}
                        </Typography>
                      </Box>
                    ) : null}
                  </Stack>
                </Stack>

                <Box
                  sx={{
                    bgcolor: 'grey.200',
                    borderRadius: 999,
                    display: 'flex',
                    height: 8,
                    mt: 1.25,
                    overflow: 'hidden',
                  }}
                >
                  <Box
                    sx={{
                      bgcolor: 'primary.main',
                      width: `${availablePercent}%`,
                    }}
                  />
                  <Box
                    sx={{
                      bgcolor: 'grey.500',
                      width: `${claimedPercent}%`,
                    }}
                  />
                </Box>
              </Box>
            );
          })}
        </Stack>
      )}
    </DetailCard>
  );
};

export const DonationImageGrid = ({ images, loading = false, ...props }) => {
  return (
    <Box
      display="grid"
      gap={2}
      gridTemplateColumns={{
        xs: 'repeat(1, 1fr)',
        sm: 'repeat(2, 1fr)',
        md: 'repeat(4, 1fr)',
      }}
      {...props}
    >
      {loading ? (
        <>
          <Skeleton width="100%" variant="rounded" height={180} />
          <Skeleton width="100%" variant="rounded" height={180} />
          <Skeleton width="100%" variant="rounded" height={180} />
          <Skeleton width="100%" variant="rounded" height={180} />
        </>
      ) : (
        images?.map((image, i) => {
          return (
            <Box key={i}>
              <Box
                sx={{
                  borderRadius: '15px',
                  overflow: 'hidden',
                  border: (t) => `1px solid ${t.palette.divider}`,
                  boxShadow: '0px 12px 24px -4px #919EAB1F',
                }}
              >
                <Box
                  component="img"
                  sx={{ objectFit: 'cover' }}
                  src={image}
                  width="100%"
                  height="180px"
                />
              </Box>
            </Box>
          );
        })
      )}
    </Box>
  );
};

const RecurringCancelOptionsDialog = ({
  open,
  onClose,
  onCancelCurrent,
  onCancelFuture,
  canCancelFuture,
  t,
}) => (
  <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
    <DialogTitle>
      {t('donations.cancel-recurring-donation', {
        defaultValue: 'Cancel recurring donation',
      })}
    </DialogTitle>
    <DialogContent>
      <Typography variant="body2" color="text.secondary">
        {t('donations.cancel-recurring-donation-helper', {
          defaultValue:
            'Choose whether to cancel only this pickup or the remaining series.',
        })}
      </Typography>

      <Stack spacing={1.25} sx={{ mt: 2 }}>
        <Button
          color="inherit"
          fullWidth
          onClick={onCancelCurrent}
          startIcon={<EventBusyRounded />}
          sx={{
            alignItems: 'flex-start',
            borderColor: 'divider',
            justifyContent: 'flex-start',
            px: 1.5,
            py: 1.25,
            textAlign: 'left',
            textTransform: 'none',
            '& .MuiButton-startIcon': { mt: 0.25 },
          }}
          variant="outlined"
        >
          <Box>
            <Typography variant="subtitle2" fontWeight={800}>
              {t('donations.cancel-this-pickup', {
                defaultValue: 'Cancel this pickup',
              })}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {t('donations.cancel-this-pickup-helper', {
                defaultValue: 'Only this scheduled donation will be cancelled.',
              })}
            </Typography>
          </Box>
        </Button>

        <Button
          color="error"
          disabled={!canCancelFuture}
          fullWidth
          onClick={onCancelFuture}
          startIcon={<EventRepeatRounded />}
          sx={{
            alignItems: 'flex-start',
            justifyContent: 'flex-start',
            px: 1.5,
            py: 1.25,
            textAlign: 'left',
            textTransform: 'none',
            '& .MuiButton-startIcon': { mt: 0.25 },
          }}
          variant="outlined"
        >
          <Box>
            <Typography variant="subtitle2" fontWeight={800}>
              {t('donations.cancel-future-pickups', {
                defaultValue: 'Cancel future pickups',
              })}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {t('donations.cancel-future-pickups-helper', {
                defaultValue:
                  'Cancel this and future incomplete donations in the series.',
              })}
            </Typography>
          </Box>
        </Button>
      </Stack>
    </DialogContent>
    <DialogActions>
      <Button onClick={onClose}>{t('common.close')}</Button>
    </DialogActions>
  </Dialog>
);

/**
 * Show details of a donation?.
 * This component expects to be the only component active in a view (about a
 * layout) when `donation` is not null.
 * This component will return null if `donation` is null.
 *
 * @param {Function} props.onBack Callback when the "Back" button is pressed
 */
export default function DonationDetails({ donationId, rescueId, onBack }) {
  const { t } = useTranslation('translation');
  const queryClient = useQueryClient();
  const { getStatus } = useDonationStatus();
  const { getType: getRecurringType } = useRecurringType();
  const { defaultWeightUnit } = useMeasureFieldUnits();
  const { user } = useAuth();
  const { trainingComplete } = useTrainingComplete();
  const theme = useTheme();
  const isMobileLayout = useMediaQuery(theme.breakpoints.down('md'));
  const viewOrg =
    user?.account_type === 'ADMIN'
      ? PATH_DASHBOARD.admin.viewOrg
      : PATH_DASHBOARD.viewOrg;
  const history = useHistory();
  const { enqueueSnackbar } = useSnackbar();

  const socket = SocketIO.getIO();
  const [showCompleteModal, setShowCompleteModal] = useState(false);
  const [updateCompletionDataLoading, setUpdateCompletionDataLoading] =
    useState(false);
  const [acceptLoading, setAcceptLoading] = useState(false);
  const [showEditConfirmModal, setShowEditConfirmModal] = useState(false);
  const [showRepostConfirmModal, setShowRepostConfirmModal] = useState(false);
  const [showCopyConfirmModal, setShowCopyConfirmModal] = useState(false);
  const [showCancelConfirmModal, setShowCancelConfirmModal] = useState(false);
  const [showRecurringCancelOptions, setShowRecurringCancelOptions] =
    useState(false);
  const [
    showCancelRecurringDonationConfirmModal,
    setShowCancelRecurringDonationConfirmModal,
  ] = useState(false);
  const [showCancelDonationConfirmModal, setShowCancelDonationConfirmModal] =
    useState(false);
  const [
    showCancelSingleInstanceDonationConfirmModal,
    setShowCancelSingleInstanceDonationConfirmModal,
  ] = useState(false);
  const [showUploadPhoto, setShowUploadPhoto] = useState(false);
  const [driverSelectorOpen, setDriverSelectorOpen] = useState(false);
  const [isDonationEditable, setIsDonationEditable] = useState(false);
  const [showCreateDonationDialog, setShowCreateDonationDialog] =
    useState(false);
  const [patternData, setPatternData] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [isRepost, setRepost] = useState(false);
  const [isCopy, setCopy] = useState(false);
  const [onboardingRequestingProfileId, setOnboardingRequestingProfileId] =
    useState(null);
  const [onboardingRequestedProfileIds, setOnboardingRequestedProfileIds] =
    useState([]);
  const {
    donation: donation_,
    isLoading: loadingDonation,
    invalidate: invalidateDonation,
    errorKind: donationErrorKind,
    unavailableReason,
    retry: retryDonation,
  } = useDonation({
    id: donationId,
    rescueId,
  });
  // When the donation belongs to another of the user's organizations, the
  // server lists which ones can see it; only those are offered as a switch.
  const { teams: userTeams, switchTo: switchToTeam } = useTeams({
    autoLoad: Boolean(unavailableReason?.switchableTeamIds?.length),
  });
  const otherTeams = getSwitchableOrganizations(
    userTeams,
    unavailableReason,
    user?.team_id
  );
  const handleSwitchOrganization = (teamId) => {
    // Keep the current URL so the donation is re-resolved under the new org.
    switchToTeam(teamId, { changeRoute: false });
  };
  const invalidate = () => {
    invalidateDonation();
    queryClient.invalidateQueries({ queryKey: ['donation-activity-logs'] });
  };
  const donation = donation_ ?? {};

  const isPartial = !!donation.is_partial;

  const isSingleRescueView = !!rescueId;
  const canManageOrgOnboarding =
    Boolean(user?.is_admin) && user?.account_type !== 'ADMIN';

  const {
    data: allRescues = [],
    isLoading: isLoadingRescues,
    refetch: refetchAllRescues,
  } = useGetPartialRescues(donation?.id, {
    enabled: !!donation?.is_partial,
  });
  // Partial rescue aggregation - used for partial rescue features
  const _allPartialRescuesAreCompleted = allRescues.every(
    (r) => r.rescue_status === 'COMPLETED'
  );
  void _allPartialRescuesAreCompleted; // Suppress unused variable warning

  const somePartialRescuesAreCompleted = allRescues.some(
    (r) => r.rescue_status === 'COMPLETED'
  );

  const _aggregatedPartialWeights = aggregatePartialRescueWeights(allRescues);
  void _aggregatedPartialWeights; // Suppress unused variable warning

  // useDonation only starts loading in an effect, and keeps the previous
  // donation for a render when the id changes. Until this id's donation is here
  // we don't know whether its donor is hidden, so nothing below may mount and
  // fetch donor-only data (notes, activity log).
  const isLoading =
    loadingDonation ||
    !donation_ ||
    donation_.id !== donationId ||
    !donationId ||
    isLoadingRescues;
  const deliveryOption = donation?.delivery_option;
  const shouldLoadOnboardingRequirements = deliveryOption !== 'STAFF-MEALS';
  const {
    requiredItems: onboardingRequiredItems,
    onboardingGroups,
    requirementsKnown: onboardingRequirementsKnown,
    refresh: refreshOnboardingRequirements,
  } = useRequiredOnobardingItems(
    shouldLoadOnboardingRequirements ? donation?.id : null
  );
  const hasOnboardingRequirements = onboardingRequiredItems.length > 0;

  useEffect(() => {
    setOnboardingRequestingProfileId(null);
    setOnboardingRequestedProfileIds([]);
  }, [donationId]);

  const handleRequestOnboarding = useCallback(
    async (complianceProfileId) => {
      if (!complianceProfileId) return;
      setOnboardingRequestingProfileId(complianceProfileId);
      try {
        await backend.team.requestOnboarding(complianceProfileId, donationId);
        await refreshOnboardingRequirements();
        setOnboardingRequestedProfileIds((prev) =>
          prev.includes(complianceProfileId)
            ? prev
            : [...prev, complianceProfileId]
        );
        enqueueSnackbar(t('donations.onboarding-request-created'), {
          variant: 'success',
        });
      } catch (err) {
        console.error(err);
        enqueueSnackbar(
          err.message || t('donations.onboarding-request-error'),
          { variant: 'error' }
        );
      } finally {
        setOnboardingRequestingProfileId(null);
      }
    },
    [donationId, enqueueSnackbar, refreshOnboardingRequirements, t]
  );
  const foodImages = useMemo(() => {
    return parseJsonArray(donation?.images);
  }, [donation?.images]);
  const additionalImages = useMemo(() => {
    return parseJsonArray(donation?.additional_images);
  }, [donation?.additional_images]);

  useEffect(() => {
    if (socket && donation) {
      socket.emit('joinRoom', {
        type: 'donation',
        id: donationId,
      });
      socket.on('updated', (id) => {
        donations.getById(id).catch(() => {
          // If its already claimed by someone else, this API returns 403 error
          enqueueSnackbar(t('donations.donation-claimed-by-someone-else'), {
            variant: 'warning',
          });
          history.push(PATH_DASHBOARD.root);
        });
      });
    }
    return () => {
      if (socket && donation) {
        socket.emit('leaveRoom', {
          type: 'donation',
          id: donation?.id,
        });
        socket.off('updated');
      }
    };
  }, [socket, donation]);

  const partialRescues = allRescues.flatMap((r) =>
    parseJsonArray(r.partial_food_types).map((partialRescue) => ({
      recipient_team_id: r.recipient_team_id,
      partial_weight_reserved: partialRescue.partial_weight_reserved,
      food_type: partialRescue.food_type,
      rescue_id: r.id,
    }))
  );

  const vehicleLabelsWithUnit = useGetVehicleLabels();
  const foodWeights = useMemo(() => {
    const weights = parseJsonArray(donation?.food_weight_object);

    return weights
      .map((food) => {
        if (food?.estimated_weight === null) {
          return {
            ...food,
            estimated_weight: 0,
          };
        }
        return food;
      })
      .map(({ food_type, estimated_weight }) => ({
        food_type: _.toLower(food_type),
        estimated_weight: {
          value: estimated_weight,
          unit: 'lbs',
        },
      }));
  }, [donation?.food_weight_object]);

  const parsedFoodWeightObject = parseJsonArray(donation?.food_weight_object);

  useEffect(() => {
    setIsDonationEditable(
      // Partials stay editable after rescues complete so the donor can keep
      // adjusting the open portion; the edit form locks the completed parts.
      donation?.delivery_option === 'STAFF-MEALS' || donation.is_partial
        ? true
        : !donation?.rescue_id ||
            ['WAITING_FOR_APPROVAL', 'ASSIGNED', 'AWAITING_DRIVER'].includes(
              donation?.rescue_status
            )
    );
  }, [donation, JSON.stringify(allRescues)]);

  const handleDelete = async () => {
    backend.admin
      .deleteDonation(donation?.id)
      .then(async (res) => {
        if (!res.ok) {
          enqueueSnackbar(t('form.errors.generic'), { variant: 'error' });
        } else {
          onBack();
        }
      })
      .catch((err) => {
        enqueueSnackbar(err.message, { variant: 'error' });
      });
  };

  const isRecipientSameAsFoodMover =
    donation?.food_mover_team_id === donation?.recipient_team_id;
  const rescuedFoodWeights = useFoodWeights(donation);

  const isDestLocationPresent =
    donation?.delivery_option === 'SELF-DELIVER' &&
    _isFinite(_get(donation, 'parsed_dest_location.lat', null));

  const donorLocation = useMemo(
    () =>
      parseJsonObject(donation?.donor_location, {
        timezone_id: 'America/Los_Angeles',
      }),
    [donation?.donor_location]
  );

  // For partial rescues - can be used for weight display
  const _estimatedWeightsForPartialRescue = parseJsonArray(
    donation?.partial_food_types
  ).map((ft) => ({
    food_type: ft.food_type,
    estimated_weight: ft.partial_weight_reserved,
    estimated_weight_unit: ft.weight_unit,
  }));
  void _estimatedWeightsForPartialRescue; // Suppress unused variable warning

  const sourceLocation = useMemo(() => {
    if (_isNumber(donation?.location?.lat)) {
      return donation.location;
    } else {
      return parseJsonObject(donation?.donor_location);
    }
  }, [donation]);
  const timeZoneId = useMemo(() => {
    return sourceLocation?.timezone_id ?? user.timezone_id;
  }, [user, sourceLocation]);

  const receiverLocation = useMemo(
    () => parseJsonObject(donation?.receiver_location),
    [donation?.receiver_location]
  );
  const foodMoverLocation = useMemo(
    () =>
      (user?.start_lat && user?.start_lng) || (user?.user_lat && user?.user_lng)
        ? {
            lat: user?.start_lat || user?.user_lat,
            lng: user?.start_lng || user?.user_lng,
          }
        : parseJsonObject(donation?.food_mover_location),
    [
      user?.user_lat,
      user?.user_lng,
      user?.start_lat,
      user?.start_lng,
      donation?.food_mover_location,
    ]
  );

  const isDonorTeam = useMemo(
    () => user?.team_id === donation?.donor_team_id,
    [user?.team_id, donation?.donor_team_id]
  );
  const isRecipientTeam = useMemo(
    () => user?.team_id === donation?.recipient_team_id,
    [user?.team_id, donation?.recipient_team_id]
  );
  const isFoodMoverTeam =
    Boolean(donation?.food_mover_team_id) &&
    user?.team_id === donation?.food_mover_team_id;
  const claimable = useMemo(() => canClaim(donation, user), [donation, user]);
  const availabilityNotice = useMemo(
    () =>
      getDonationAvailabilityNotice({
        donation: donation_,
        rescueId,
        claimable,
        viewer: {
          isDonorTeam,
          isFoodMoverTeam,
          isReadOnly: user?.is_corporate || user?.account_type === 'ADMIN',
        },
      }),
    [
      donation_,
      rescueId,
      claimable,
      isDonorTeam,
      isFoodMoverTeam,
      user?.is_corporate,
      user?.account_type,
    ]
  );
  // Claiming an expired donation can only fail, so hide the button.
  const canClaimDonation = claimable && !isClaimWindowClosed(donation_);
  const canViewOnboardingRequirements = useMemo(
    () =>
      canViewDonationOnboardingRequirements({
        deliveryOption,
        isRecipientTeam,
        canClaimDonation,
      }),
    [canClaimDonation, deliveryOption, isRecipientTeam]
  );

  // Donor should be able to cancel food runs only if they are converted runs
  const cancelConditionForDonor = useMemo(
    () =>
      isDonorTeam &&
      ((donation?.is_food_run &&
        (donation?.converted_to_food_run ||
          donation?.rp_converted_to_food_run)) ||
        !donation?.is_food_run),
    [donation, isDonorTeam]
  );
  const canCancelRecurringDonation =
    Boolean(donation?.is_recurring) &&
    !user.is_driver &&
    (cancelConditionForDonor || donation?.food_mover_team_id === user.team_id);
  const canCancelFutureRecurringDonations =
    canCancelRecurringDonation && !donation?.is_pattern_cancelled;

  const handleDriverAssign = ({ drivers }) => {
    rescues
      .assignDriverToIncompleteDonation({
        drivers,
        donationId: donation?.id,
        rescueId: donation?.rescue_id,
      })
      .then((data) => {
        enqueueSnackbar(data.message, { variant: 'success' });
        setDriverSelectorOpen(false);
        invalidate();
        setShowCompleteModal(true);
      })
      .catch((err) => enqueueSnackbar(err.message, { variant: 'error' }));
  };

  const handleEditRescueDetails = () => {
    if (!donation?.rescue_drivers?.length) {
      setDriverSelectorOpen(true);
    } else {
      setShowCompleteModal(true);
    }
  };

  const handleEditDonationSingleInstance = () => {
    const query = donation?.rescue_id ? `?rescueId=${donation.rescue_id}` : '';
    history.push(
      `${PATH_DASHBOARD.editDonationSingleInstance(donation?.id)}${query}`
    );
  };

  const handleEditFutureDonations = () =>
    history.push(PATH_DASHBOARD.editRecurringDonation(donation?.parent_id));

  // Route through reason-collecting modals (backend rejects cancels without reason).
  const handleCancelFutureDonations = () => {
    setShowCancelConfirmModal(false);
    setShowCancelRecurringDonationConfirmModal(true);
  };

  const handleCancelSingleInstanceDonation = () => {
    setShowCancelSingleInstanceDonationConfirmModal(false);
    setShowCancelDonationConfirmModal(true);
  };

  const handleCancelCurrentRecurringDonation = () => {
    setShowRecurringCancelOptions(false);
    setShowCancelDonationConfirmModal(true);
  };

  const handleCancelFutureRecurringDonations = () => {
    setShowRecurringCancelOptions(false);
    setShowCancelRecurringDonationConfirmModal(true);
  };

  const handleCopyDonation = () => {
    if (donation.parent_id) {
      backend.donations
        .getRecurringPatternById({
          id: donation.parent_id,
        })
        .then((data) => {
          setPatternData(data);
          setCopy(true);
          setShowCreateDonationDialog(true);
          setShowCopyConfirmModal(false);
        });
    } else {
      setCopy(true);
      setShowCreateDonationDialog(true);
      setShowCopyConfirmModal(false);
    }
  };

  const handleUpdateCompletionData = (updates) => {
    setUpdateCompletionDataLoading(true);
    return foodRun
      .updateCompletionData({ ...updates, rescueId: donation?.rescue_id })
      .then(() => {
        setShowCompleteModal(false);
        enqueueSnackbar(t('donations.donation-updated'), {
          variant: 'success',
        });
        invalidate();
      })
      .catch((err) => {
        enqueueSnackbar(err?.message || t('form.errors.generic'), {
          variant: 'error',
        });
        console.error(err);
      })
      .finally(() => setUpdateCompletionDataLoading(false));
  };

  const handleComplete = (completeData) => {
    setUpdateCompletionDataLoading(true);

    if (!donation?.is_food_run) {
      return rescues
        .complete({
          ...completeData,
          donationId,
          rescueId: donation?.rescue_id,
          isPartialRescue: donation?.is_partial,
        })
        .then(() => {
          if (completeData.donorRating) {
            backend.donations
              .insertRating({
                rating: completeData.donorRating,
                notes: completeData.donorRatingNotes,
                teamId: donation?.donor_team_id,
                rescueId: donation?.rescue_id,
              })
              .then(invalidate);
          }
          setShowCompleteModal(false);
          enqueueSnackbar(t('donations.rescue-completed'), {
            variant: 'success',
          });
          invalidate();
        })
        .catch((err) => {
          enqueueSnackbar(err?.message || t('form.errors.generic'), {
            variant: 'error',
          });
          console.error(err);
        })
        .finally(() => setUpdateCompletionDataLoading(false));
    } else {
      return foodRun
        .completeFoodRun({
          ...completeData,
          rescueId: donation?.rescue_id,
          donorTeam: donation?.donor_team_id,
          receiverTeam: donation?.recipient_team_id,
        })
        .then(() => {
          setShowCompleteModal(false);
          enqueueSnackbar(t('donations.food-run-completed'), {
            variant: 'success',
          });
          invalidate();
        })
        .catch((err) => {
          enqueueSnackbar(err?.message || t('form.errors.generic'), {
            variant: 'error',
          });
          console.error(err);
        })
        .finally(() => setUpdateCompletionDataLoading(false));
    }
  };

  useEffect(() => {
    if (!showCreateDonationDialog) {
      setRepost(false);
      setCopy(false);
    }
  }, [showCreateDonationDialog]);

  const emitInvalidateIncomingRequests = useEventBus(
    'incoming-request',
    'invalidate'
  );
  const handleAcceptRescue = async ({
    recipientTeamId,
    pickupInstructions,
    dropoffInstructions,
    convertSingleDonation,
    drivers,
    isPartialRescue,
  }) => {
    setAcceptLoading(true);
    if (recipientTeamId) {
      return donations
        .convertToFoodRun({
          recipientTeamId,
          parentId: donation.parent_id,
          drivers,
          donorTeamId: donation.donor_team_id,
          pickupInstructions,
          dropoffInstructions,
          donationId: convertSingleDonation ? donation.id : null,
        })
        .then(() => {
          invalidate();
          emitInvalidateIncomingRequests();
          history.push(PATH_DASHBOARD.viewFoodRun(donationId));
        })
        .catch(() => {
          enqueueSnackbar(t('form.errors.generic'), {
            variant: 'error',
          });
        })
        .finally(() => setAcceptLoading(false));
    } else {
      return rescues
        .accept({
          donationId,
          rescueId: donation.rescue_id,
          drivers,
          isPartialRescue,
        })
        .then((result) => {
          enqueueSnackbar(
            t(
              result?.action === 'drivers-reassigned'
                ? 'donations.drivers-updated'
                : 'donations.rescue-accepted'
            ),
            {
              variant: 'success',
            }
          );
          invalidate();
          emitInvalidateIncomingRequests();
        })
        .catch((err) => {
          enqueueSnackbar(err?.message || t('form.errors.generic'), {
            variant: 'error',
          });
          console.error(err);
        })
        .finally(() => setAcceptLoading(false));
    }
  };

  const isReadOnly = useMemo(
    () => user.is_corporate || user.account_type === 'ADMIN',
    [user]
  );
  const isStarted = moment(donation?.start_date)
    .tz(timeZoneId)
    .isBefore(moment().tz(timeZoneId));

  const destinationLocation = receiverLocation;

  const rescueImages = useMemo(
    () =>
      parseJsonArray(donation?.rescue_images).map(
        (keyName) => `${S3_BASE_URL}/${BUCKET_NAME}/${keyName}`
      ),
    [donation?.rescue_images]
  );
  const hasDonationPhotos =
    foodImages.length > 0 ||
    additionalImages.length > 0 ||
    (!!donation?.rescue_id && rescueImages.length > 0);

  // Claim, unclaim and unassign all go through here, like mobile's triggerSocket.
  const handleRescueStatusChange = useCallback(() => {
    socket.emit('rescueUpdate', {
      donationId: donation.id,
      donorTeam: donation.donor_team_id,
      rescuerTeam: donation.recipient_team_id,
    });
    emitDonationUpdate({
      donationId: donation.id,
      teamId: user.team_id,
      isFoodRun: donation.is_food_run,
    });
    invalidate();
    refetchAllRescues();
  }, [donation, invalidate, user]);

  const detailFoodWeightObject =
    !isPartial &&
    (donation?.donation_status === 'COMPLETED' ||
      donation?.donation_status === 'RESCUED')
      ? groupByUnit(rescuedFoodWeights)
      : parsedFoodWeightObject;
  const foodLedgerRows = buildFoodLedgerRows({
    donation,
    parsedFoodWeightObject: detailFoodWeightObject,
    allRescues,
    defaultUnit: defaultWeightUnit?.value,
  });
  const donationWindow = formatDonationWindow({ donation, timeZoneId });
  const recurringSchedule = getRecurringScheduleInfo({
    donation,
    getRecurringType,
    t,
  });
  const vehicleLabel = donation?.vehicle
    ? vehicleLabelsWithUnit.find((item) => item.vehicle === donation?.vehicle)
        ?.label || t('common.na')
    : t('common.na');
  const donationTypeLabel = getDeliveryOptionLabel(
    t,
    donation?.delivery_option
  );
  const shouldShowRecipientInfo =
    Boolean(donation?.recipient_team_id) &&
    donation?.delivery_option !== 'STAFF-MEALS' &&
    (donation?.is_partial ? isSingleRescueView : true);
  const shouldShowDonationChat = Boolean(donation?.rescue_id) && !isReadOnly;
  const showChatWithDonorInfo =
    shouldShowDonationChat &&
    (user.team_id === donation?.recipient_team_id || !shouldShowRecipientInfo);
  const showChatWithRecipientInfo =
    shouldShowDonationChat && shouldShowRecipientInfo && !showChatWithDonorInfo;
  const instructionItems = [
    {
      show: donation?.delivery_option === 'RECEIVER-PICKUP',
      label: t('donations.pickup-instructions'),
      value: donation?.pickup_instructions,
    },
    {
      show: donation?.delivery_option === 'SELF-DELIVER',
      label: t('donations.dropoff-instructions'),
      value: donation?.dropoff_instructions,
    },
    {
      show: true,
      label: t('donations.notes'),
      value: donation?.notes,
    },
  ].filter((item) => item.show && item.value);
  const donorMapLocation =
    donorLocation?.formatted_address || donorLocation?.place_id
      ? donorLocation
      : sourceLocation;
  const recipientMapLocation = donation?.recipient_team_id
    ? destinationLocation
    : null;
  const mapInfoItems = [
    isDonorHidden(donation)
      ? {
          label: t('donations.approximate-pickup-area'),
          value: donorMapLocation?.formatted_address,
          location: null,
        }
      : {
          label: t('donations.pickup-location', {
            defaultValue: 'Pickup location',
          }),
          value:
            donorMapLocation?.formatted_address ||
            (Number.isFinite(donorMapLocation?.lat) &&
            Number.isFinite(donorMapLocation?.lng)
              ? `${donorMapLocation.lat}, ${donorMapLocation.lng}`
              : null),
          location: donorMapLocation,
        },
    {
      label: t('donations.dropoff-location', {
        defaultValue: 'Dropoff location',
      }),
      value: donation?.recipient_team_id
        ? recipientMapLocation?.formatted_address
        : null,
      location: recipientMapLocation,
    },
    {
      label: t('common.timezone', { defaultValue: 'Time zone' }),
      value: timeZoneId,
    },
  ].filter((item) => item.value);
  const canShowPrimaryUpdateWeights = canCompleteRescue(donation, user);
  const canShowSecondaryUpdateWeights =
    !!donation.rescue_id &&
    RESCUE_STATUSES_ACCEPTED_FOR_WEIGHTS.includes(donation?.rescue_status) &&
    [
      donation?.recipient_team_id,
      donation?.food_mover_team_id,
      donation?.donor_team_id,
    ].includes(user.team_id) &&
    user.account_type !== 'ADMIN' &&
    (isStarted ||
      moment()
        .tz(timeZoneId)
        .isSame(moment(donation?.start_date).tz(timeZoneId), 'day'));
  const canShowUpdateWeights =
    canShowPrimaryUpdateWeights || canShowSecondaryUpdateWeights;
  const handleUpdateWeightsClick = () => {
    if (canShowPrimaryUpdateWeights) {
      setShowCompleteModal(true);
      return;
    }

    handleEditRescueDetails();
  };
  const updateWeightsActionLabel = t('donations.add-weights');
  const donationAmountsAction = canShowUpdateWeights ? (
    <Button
      color="primary"
      onClick={handleUpdateWeightsClick}
      size="small"
      startIcon={<MonitorWeightOutlined />}
      sx={{
        borderRadius: 1,
        boxShadow: 'none',
        fontWeight: 800,
        minHeight: 34,
        px: 1.5,
        whiteSpace: 'nowrap',
      }}
      variant="outlined"
    >
      {updateWeightsActionLabel}
    </Button>
  ) : null;
  const canReassignRescue =
    !canShowPrimaryUpdateWeights &&
    canAcceptRescue(donation, user) &&
    donation?.rescue_status === 'ASSIGNED';
  const isCurrentUserAssignedDriver = donation?.rescue_drivers?.some(
    (driver) => String(driver.user_id) === String(user.id)
  );
  const canDriverSelfUnassign =
    Boolean(user.is_driver) &&
    isCurrentUserAssignedDriver &&
    !!donation?.rescue_id &&
    !['CANCELLED', 'COMPLETED'].includes(donation?.rescue_status);
  const canAcceptRescueRequest =
    !canShowPrimaryUpdateWeights &&
    canAcceptRescue(donation, user) &&
    donation?.rescue_status !== 'ASSIGNED';
  const donationActionControls =
    !isLoading && !isReadOnly ? (
      <Stack
        data-tour="donation-actions-area"
        direction="column"
        alignItems="stretch"
        gap={1}
        sx={{ minWidth: 0, width: '100%' }}
      >
        <Stack
          direction="row"
          alignItems="center"
          gap={1}
          flexWrap="wrap"
          sx={{
            width: '100%',
            '& > .MuiButton-root, & > span': {
              flex: {
                xs: '1 1 100%',
                sm: '1 1 168px',
                lg: '0 1 auto',
              },
              minWidth: { xs: '100%', sm: 0 },
            },
            '& .MuiButton-root': {
              borderRadius: 1,
              fontWeight: 800,
              justifyContent: 'center',
              lineHeight: 1.2,
              maxWidth: '100%',
              minHeight: 36,
              textAlign: 'center',
              whiteSpace: 'normal',
            },
            '& > span > .MuiButton-root': {
              width: '100%',
            },
            '& .MuiButton-startIcon': {
              flexShrink: 0,
              mr: 0.75,
            },
            '& .MuiIconButton-root': {
              border: (theme) => `1px solid ${theme.palette.divider}`,
              borderRadius: 1,
              color: 'text.secondary',
              height: 36,
              width: 36,
              '&:hover': {
                bgcolor: 'action.hover',
                color: 'text.primary',
              },
            },
          }}
        >
          {canShowUpdateWeights ? (
            <Button
              color="primary"
              onClick={handleUpdateWeightsClick}
              startIcon={<MonitorWeightOutlined />}
              variant="contained"
            >
              {updateWeightsActionLabel}
            </Button>
          ) : null}

          {canStartRescue(donation, user) ? (
            <StartRescue
              donation={donation}
              donationId={donation.id}
              rescueId={donation.rescue_id}
              onStart={invalidate}
            />
          ) : null}

          {canAcceptRescueRequest ? (
            <AcceptDonation
              onAccept={handleAcceptRescue}
              donation={donation}
              loading={acceptLoading}
              trainingComplete={trainingComplete}
            />
          ) : null}

          {canClaimDonation ? (
            <ClaimDonation
              donationId={donation.id}
              donation={donation}
              onClaim={handleRescueStatusChange}
              disabled={
                hasOnboardingRequirements || !onboardingRequirementsKnown
              }
            />
          ) : null}
          {canUnclaim(donation, user) ? (
            <UnclaimDonation
              donation={donation}
              onUnclaim={handleRescueStatusChange}
            />
          ) : null}

          <MoreButton
            data-tour="donation-more-actions"
            sx={{
              color: 'text.secondary',
            }}
            Icon={MoreVert}
            options={[
              {
                showIf:
                  !donation?.is_food_run &&
                  isDonorTeam &&
                  (donation.is_partial
                    ? !somePartialRescuesAreCompleted
                    : donation?.rescue_status !== 'COMPLETED'),
                icon: <RefreshRounded />,
                text: t('donations.repost'),
                dataTour: 'donation-repost-option',
                action: () => setShowRepostConfirmModal(true),
              },
              {
                showIf: isDonationEditable && isDonorTeam,
                icon: <CreateRounded />,
                text: t('donations.edit-donation'),
                dataTour: 'donation-edit-option',
                action: () =>
                  donation?.is_recurring &&
                  !donation?.is_pattern_cancelled &&
                  !user.is_driver
                    ? setShowEditConfirmModal(true)
                    : handleEditDonationSingleInstance(),
              },
              {
                showIf: isDonorTeam,
                icon: <CopyAll />,
                text: t('donations.copy-donation'),
                action: () => {
                  setCopy(true);
                  setShowCreateDonationDialog(true);
                },
              },
              {
                showIf:
                  (donation?.is_recurring === 0 &&
                    cancelConditionForDonor &&
                    !user.is_driver) ||
                  canCancelRecurringDonation,
                icon: <CloseRounded />,
                text: t('donations.cancel-donation'),
                dataTour: 'donation-cancel-option',
                action: () =>
                  donation?.is_recurring
                    ? setShowRecurringCancelOptions(true)
                    : setShowCancelDonationConfirmModal(true),
              },
            ]}
            displayButtonForSingleOption={false}
          />
        </Stack>
        {canClaimDonation &&
        canViewOnboardingRequirements &&
        hasOnboardingRequirements ? (
          <Typography variant="body2" color="text.secondary">
            {t('donations.unavailable.requirements-not-met')}{' '}
            <Link
              component="button"
              type="button"
              variant="body2"
              sx={{ verticalAlign: 'baseline' }}
              onClick={() =>
                document
                  .getElementById(DONATION_REQUIREMENTS_ELEMENT_ID)
                  ?.scrollIntoView({ behavior: 'smooth', block: 'center' })
              }
            >
              {t('donations.unavailable.view-requirements')}
            </Link>
          </Typography>
        ) : null}
      </Stack>
    ) : null;
  // The log names the donor, so it waits for the claim like the rest.
  const activityLogCard =
    !isLoading && !isDonorHidden(donation) ? (
      <DetailCard title={t('donations.activity-log')}>
        <DonationEventLogs
          donationId={donationId}
          rescueId={donation?.rescue_id}
          status={donation?.rescue_status}
          tz={donorLocation?.timezone_id}
          isCurrentTeamDonor={donation?.donor_team_id === user.team_id}
          recipientTeamId={donation?.recipient_team_id}
        />
      </DetailCard>
    ) : null;

  if (donationErrorKind) {
    return (
      <DonationUnavailable
        kind={donationErrorKind}
        reason={unavailableReason}
        donationId={donationId}
        onRetry={retryDonation}
        organizationName={user?.business_name}
        otherOrganizations={otherTeams}
        onSwitchOrganization={handleSwitchOrganization}
      />
    );
  }

  return (
    <>
      <DialogCreateDonation
        open={showCreateDonationDialog}
        onClose={() => {
          setShowCreateDonationDialog(false);
          setCopy(false);
          setRepost(false);
          setPatternData(null);
        }}
        initialDonation={donation}
        isCopy={isCopy}
        isRepost={isRepost}
        patternData={patternData}
        partialRescues={partialRescues}
      />
      <UploadPhotoDonation
        open={showUploadPhoto}
        donationId={donationId}
        isRecurring={donation?.is_recurring}
        close={() => setShowUploadPhoto(false)}
        invalidate={invalidate}
        patternId={donation?.parent_id}
      />
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Button startIcon={<ChevronLeft />} onClick={onBack}>
          {t('donations.back')}
        </Button>
      </Stack>

      {!isLoading && !isReadOnly ? (
        <DonationAvailabilityNotice
          notice={availabilityNotice}
          donationId={donationId}
        />
      ) : null}

      {canViewOnboardingRequirements &&
        hasOnboardingRequirements &&
        !isLoading &&
        !isReadOnly &&
        (() => {
          // Pre-compute group data for all onboarding groups
          const groupRows = onboardingGroups.map((group) => {
            const complianceProfileId = group?.complianceProfileId;
            const gatekeeperTeamId = group?.gatekeeperTeamId;
            const hasRequestedOnboarding =
              onboardingRequestedProfileIds.includes(complianceProfileId);
            const hasAssignedOnboardingRequest = Boolean(
              group?.hasOnboardingRequest
            );
            const hasPendingOrRequestedOnboardingRequest =
              hasAssignedOnboardingRequest ||
              Boolean(group?.hasIncomingOnboardingRequest) ||
              hasRequestedOnboarding;
            const hasPendingOnboardingRequest =
              !hasAssignedOnboardingRequest &&
              (Boolean(group?.hasIncomingOnboardingRequest) ||
                hasRequestedOnboarding);
            const supportsOnboardingRequestFlow =
              group?.gatekeeperType === 'MUNICIPALITY' ||
              group?.gatekeeperType === 'CORPORATE';
            const canRequestOnboarding =
              Boolean(complianceProfileId) &&
              Boolean(gatekeeperTeamId) &&
              supportsOnboardingRequestFlow;
            const shouldShowManagerContactMessage =
              Boolean(complianceProfileId) &&
              Boolean(gatekeeperTeamId) &&
              !supportsOnboardingRequestFlow &&
              !hasPendingOrRequestedOnboardingRequest;
            const onboardingLinkPath =
              complianceProfileId && gatekeeperTeamId
                ? `${PATH_DASHBOARD.corporateOnboarding.request(
                    complianceProfileId,
                    gatekeeperTeamId
                  )}?type=DONOR&gatekeeperType=${
                    group?.gatekeeperType || 'CORPORATE'
                  }`
                : undefined;
            const canViewOnboardingLink =
              canManageOrgOnboarding &&
              hasAssignedOnboardingRequest &&
              Boolean(onboardingLinkPath);
            const managerName =
              group?.gatekeeperName ||
              group?.complianceProfileTitle ||
              t('donations.onboarding-manager-fallback');
            const requiredItemCount = Number(group?.requiredItemCount || 0);
            const completedItemCount = Array.isArray(group?.items)
              ? group.items.filter((item) => Boolean(item?.completed)).length
              : 0;
            // Count items where NPO submitted but awaiting corporate verification
            const submittedItemCount = Array.isArray(group?.items)
              ? group.items.filter(
                  (item) => !item?.completed && item?.completed_by
                ).length
              : 0;
            const hasAnyProgress =
              completedItemCount > 0 || submittedItemCount > 0;
            const progressPercent =
              requiredItemCount > 0
                ? Math.round(
                    ((completedItemCount + submittedItemCount) /
                      requiredItemCount) *
                      100
                  )
                : 0;
            const hasSubmittedItemsForReview = Array.isArray(group?.items)
              ? group.items.some(
                  (item) =>
                    Boolean(item?.is_verification_required) &&
                    ((item?.completed && item?.is_accepted !== true) ||
                      (!item?.completed && item?.completed_by))
                )
              : false;
            const remainingItemCount = Math.max(
              requiredItemCount - completedItemCount - submittedItemCount,
              0
            );
            const isComplete =
              requiredItemCount > 0 &&
              remainingItemCount === 0 &&
              !hasSubmittedItemsForReview;
            const isUnderReview =
              hasSubmittedItemsForReview && remainingItemCount === 0;
            const isInProgress =
              hasAnyProgress && !isUnderReview && !isComplete;
            const hasRejectedOnboardingRequest = Boolean(
              group?.hasRejectedOnboardingRequest
            );
            const rejectedOnboardingRequestReason =
              group?.rejectedOnboardingRequestReason;
            const isNotStarted =
              !hasRejectedOnboardingRequest &&
              !shouldShowManagerContactMessage &&
              !hasPendingOnboardingRequest &&
              !isUnderReview &&
              !isComplete &&
              !isInProgress;

            let statusColor, statusBg, statusIcon, statusLabel;
            if (hasRejectedOnboardingRequest) {
              statusColor = 'error.main';
              statusBg = 'error.lighter';
              statusIcon = <ErrorOutline sx={{ fontSize: 14 }} />;
              statusLabel = t('donations.onboarding-status-rejected', {
                defaultValue: 'Rejected',
              });
            } else if (shouldShowManagerContactMessage) {
              statusColor = 'info.main';
              statusBg = 'info.lighter';
              statusIcon = <HourglassEmpty sx={{ fontSize: 14 }} />;
              statusLabel = t('donations.onboarding-status-contact-required', {
                defaultValue: 'Contact Required',
              });
            } else if (hasPendingOnboardingRequest) {
              statusColor = 'warning.main';
              statusBg = 'warning.lighter';
              statusIcon = <HourglassEmpty sx={{ fontSize: 14 }} />;
              statusLabel = t('donations.onboarding-status-pending', {
                defaultValue: 'Awaiting Approval',
              });
            } else if (isUnderReview) {
              statusColor = 'info.main';
              statusBg = 'info.lighter';
              statusIcon = <TaskAlt sx={{ fontSize: 14 }} />;
              statusLabel = t('donations.onboarding-status-under-review', {
                defaultValue: 'Under Review',
              });
            } else if (isComplete) {
              statusColor = 'success.main';
              statusBg = 'success.lighter';
              statusIcon = <TaskAlt sx={{ fontSize: 14 }} />;
              statusLabel = t('donations.onboarding-status-complete', {
                defaultValue: 'Complete',
              });
            } else if (isInProgress) {
              statusColor = 'primary.main';
              statusBg = 'primary.lighter';
              statusIcon = <CheckCircleOutline sx={{ fontSize: 14 }} />;
              statusLabel = `${completedItemCount}/${requiredItemCount}`;
            } else {
              statusColor = 'text.secondary';
              statusBg = 'grey.100';
              statusIcon = null;
              statusLabel = t('donations.onboarding-status-not-started', {
                defaultValue: 'Not Started',
              });
            }

            return {
              complianceProfileId,
              complianceProfileTitle: group?.complianceProfileTitle || '',
              gatekeeperTeamId,
              managerName,
              statusColor,
              statusBg,
              statusIcon,
              statusLabel,
              isComplete,
              isInProgress,
              isNotStarted,
              progressPercent,
              canViewOnboardingLink,
              onboardingLinkPath,
              hasRejectedOnboardingRequest,
              rejectedOnboardingRequestReason,
              hasPendingOrRequestedOnboardingRequest,
              canRequestOnboarding,
              completedItemCount,
              requiredItemCount,
            };
          });

          // Action button for a group row
          const renderAction = (row) => {
            if (
              row.canViewOnboardingLink &&
              !row.isComplete &&
              !row.hasRejectedOnboardingRequest
            ) {
              return (
                <Button
                  component={RouterLink}
                  to={row.onboardingLinkPath}
                  variant="outlined"
                  size="small"
                  sx={{
                    borderColor: '#bf360c',
                    color: '#bf360c',
                    backgroundColor: 'transparent',
                    fontSize: '12px',
                    minWidth: 'fit-content',
                    px: 1,
                    '&:hover': {
                      backgroundColor: '#bf360c',
                      color: 'white',
                      borderColor: '#bf360c',
                    },
                  }}
                >
                  {row.isInProgress
                    ? t('donations.onboarding-continue-button', {
                        defaultValue: 'Continue',
                      })
                    : t('donations.onboarding-start-verification-button', {
                        defaultValue: 'Start Verification',
                      })}
                </Button>
              );
            }
            if (
              !row.isComplete &&
              !row.hasPendingOrRequestedOnboardingRequest &&
              !row.hasRejectedOnboardingRequest &&
              row.canRequestOnboarding
            ) {
              return (
                <LoadingButton
                  variant="outlined"
                  size="small"
                  sx={{
                    borderColor: '#bf360c',
                    color: '#bf360c',
                    backgroundColor: 'transparent',
                    fontSize: '12px',
                    minWidth: 'fit-content',
                    px: 1,
                    '&:hover': {
                      backgroundColor: '#bf360c',
                      color: 'white',
                      borderColor: '#bf360c',
                    },
                  }}
                  onClick={() =>
                    handleRequestOnboarding(row.complianceProfileId)
                  }
                  loading={
                    onboardingRequestingProfileId === row.complianceProfileId
                  }
                  disabled={Boolean(onboardingRequestingProfileId)}
                >
                  {t('donations.request-onboarding')}
                </LoadingButton>
              );
            }
            return null;
          };
          const shouldShowStatusLabel = (row) =>
            !(row.isNotStarted && row.canViewOnboardingLink);

          const totalGroups = groupRows.length;
          const duplicateGatekeeperIds = new Set();
          {
            const counts = {};
            groupRows.forEach((row) => {
              if (row.gatekeeperTeamId) {
                counts[row.gatekeeperTeamId] =
                  (counts[row.gatekeeperTeamId] || 0) + 1;
              }
            });
            Object.entries(counts).forEach(([id, count]) => {
              if (count > 1) duplicateGatekeeperIds.add(id);
            });
          }
          return (
            <Box
              id={DONATION_REQUIREMENTS_ELEMENT_ID}
              sx={{
                position: 'relative',
                borderRadius: 2,
                p: 2.7,
                mt: 2,
                boxShadow: (th) => th.shadows[1],
                background: '#fff3e0',
                display: { xs: 'flex', sm: 'block' },
                flexDirection: { xs: 'column', sm: 'unset' },
                alignItems: { xs: 'center', sm: 'unset' },
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: (th) => th.shadows[8],
                },
                transition: 'all 0.3s ease-in-out',
              }}
            >
              <Box
                sx={{
                  position: { xs: 'static', sm: 'absolute' },
                  top: { sm: 16 },
                  right: { sm: 16 },
                  mb: { xs: 2, sm: 0 },
                  width: { xs: '100%', sm: 'auto' },
                  display: 'flex',
                  justifyContent: { xs: 'center', sm: 'flex-end' },
                  alignItems: 'center',
                  gap: 1,
                }}
              >
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    display: 'flex',
                    borderRadius: '50%',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background:
                      'linear-gradient(135deg, rgba(191, 54, 12, 0) 0%, rgba(191, 54, 12, 0.24) 97.35%)',
                    flexShrink: 0,
                  }}
                >
                  <VerifiedUser sx={{ fontSize: 28, color: '#bf360c' }} />
                </Box>
              </Box>

              <Stack sx={{ minWidth: 0 }}>
                <Typography
                  fontWeight="700"
                  variant="h4"
                  sx={{
                    color: '#bf360c',
                    fontSize: { xs: '16px', sm: '20px' },
                    mb: 0.5,
                    pr: { xs: 7, sm: 8 },
                  }}
                >
                  {onboardingGroups.length === 0 ? (
                    t('donations.onboarding-required-title')
                  ) : totalGroups === 1 ? (
                    <>
                      <Box
                        component="span"
                        sx={{
                          fontWeight: 800,
                          borderBottom: '2px solid #bf360c',
                          mr: 1,
                        }}
                      >
                        {groupRows[0].managerName}
                      </Box>
                      {t('donations.onboarding-verification-label', {
                        defaultValue: 'Verification Required',
                      })}
                    </>
                  ) : (
                    <>
                      <Box
                        component="span"
                        sx={{
                          fontSize: { xs: '18px', sm: '22px' },
                          fontWeight: 800,
                          borderBottom: '2px solid #bf360c',
                          mr: 1,
                        }}
                      >
                        {totalGroups}
                      </Box>
                      {t('donations.onboarding-verifications-required', {
                        defaultValue: 'Verifications Required',
                      })}
                    </>
                  )}
                </Typography>

                <Typography
                  variant="body2"
                  sx={{
                    color: '#8d2400',
                    fontSize: { xs: '12px', sm: '14px' },
                    pr: { xs: 7, sm: 8 },
                  }}
                >
                  {onboardingGroups.length === 0 ? (
                    <Trans
                      i18nKey="donations.you-must-complete-onboarding"
                      values={{
                        name:
                          donation.donor_business_name ||
                          t('donations.this-donor'),
                      }}
                      components={{
                        1: (
                          // eslint-disable-next-line jsx-a11y/anchor-has-content
                          <a
                            href="https://help.careit.com/"
                            target="_blank"
                            rel="noopener noreferrer"
                          />
                        ),
                      }}
                    />
                  ) : (
                    t('donations.onboarding-required-description', {
                      defaultValue:
                        '{{name}} requires your organization to be verified before you can rescue food from this location.',
                      name:
                        donation?.donor_business_name ||
                        t('donations.onboarding-manager-fallback'),
                    })
                  )}
                </Typography>

                {/* Group rows for multiple profiles */}
                {totalGroups > 1 && (
                  <Stack mt={2} spacing={0} divider={<Divider />}>
                    {groupRows.map((row, index) => (
                      <Stack
                        key={`${row.complianceProfileId ?? row.gatekeeperTeamId ?? `group-${index}`}`}
                        direction={{ xs: 'column', sm: 'row' }}
                        alignItems={{ xs: 'flex-start', sm: 'center' }}
                        spacing={{ xs: 0.5, sm: 1.5 }}
                        sx={{ py: 1.25 }}
                      >
                        <Typography
                          variant="body2"
                          fontWeight={600}
                          noWrap
                          sx={{
                            flex: { sm: 1 },
                            minWidth: 0,
                            color: '#bf360c',
                          }}
                        >
                          {row.managerName}
                          {duplicateGatekeeperIds.has(row.gatekeeperTeamId) &&
                            row.complianceProfileTitle && (
                              <Typography
                                component="span"
                                variant="body2"
                                sx={{
                                  fontWeight: 400,
                                  color: '#8d2400',
                                  ml: 0.5,
                                }}
                              >
                                ({row.complianceProfileTitle})
                              </Typography>
                            )}
                        </Typography>

                        {shouldShowStatusLabel(row) ? (
                          <Typography
                            variant="caption"
                            sx={{
                              flexShrink: 0,
                              fontWeight: 600,
                              color: row.canViewOnboardingLink
                                ? '#8d2400'
                                : '#bf360c',
                            }}
                          >
                            {row.statusLabel}
                          </Typography>
                        ) : null}

                        {renderAction(row)}
                      </Stack>
                    ))}
                  </Stack>
                )}

                {/* Progress bar for single in-progress group */}
                {totalGroups === 1 && groupRows[0].isInProgress && (
                  <LinearProgress
                    variant="determinate"
                    value={groupRows[0].progressPercent}
                    sx={{
                      mt: 1.5,
                      height: 6,
                      borderRadius: 3,
                      backgroundColor: 'rgba(191, 54, 12, 0.12)',
                      '& .MuiLinearProgress-bar': {
                        backgroundColor: '#bf360c',
                      },
                    }}
                  />
                )}

                {/* Rejection reason for single group */}
                {totalGroups === 1 &&
                  groupRows[0].hasRejectedOnboardingRequest &&
                  groupRows[0].rejectedOnboardingRequestReason && (
                    <Typography
                      variant="caption"
                      sx={{ color: '#8d2400', mt: 0.5, display: 'block' }}
                    >
                      {groupRows[0].rejectedOnboardingRequestReason}
                    </Typography>
                  )}

                {/* Single group status */}
                {totalGroups === 1 && (
                  <Stack
                    direction="row"
                    alignItems="center"
                    spacing={1}
                    flexWrap="wrap"
                    sx={{ mt: 1 }}
                  >
                    {shouldShowStatusLabel(groupRows[0]) ? (
                      <Typography
                        variant="caption"
                        sx={{
                          color: groupRows[0].canViewOnboardingLink
                            ? '#8d2400'
                            : '#bf360c',
                          fontWeight: 600,
                        }}
                      >
                        {groupRows[0].statusLabel}
                      </Typography>
                    ) : null}
                    {renderAction(groupRows[0])}
                  </Stack>
                )}
              </Stack>
            </Box>
          );
        })()}

      {isLoading ? (
        <Grid mt={2} container spacing={2.5}>
          <Grid size={{ xs: 12, md: 7.5 }}>
            <Stack spacing={2.5}>
              <Skeleton variant="rounded" height={220} />
              <Skeleton variant="rounded" height={320} />
            </Stack>
          </Grid>
          <Grid size={{ xs: 12, md: 4.5 }}>
            <Stack spacing={2.5}>
              <Skeleton variant="rounded" height={180} />
              <Skeleton variant="rounded" height={300} />
              <Skeleton variant="rounded" height={320} />
            </Stack>
          </Grid>
        </Grid>
      ) : (
        <Grid mt={2} container spacing={2.5} alignItems="flex-start">
          <Grid size={{ xs: 12, md: 7.5 }}>
            <Stack spacing={2.5}>
              <DetailCard sx={{ p: { xs: 2, md: 3 } }}>
                <Stack spacing={2.25}>
                  <Box>
                    <Stack
                      direction={{ xs: 'column', sm: 'row' }}
                      alignItems={{ xs: 'flex-start', sm: 'flex-start' }}
                      justifyContent="space-between"
                      spacing={1.5}
                    >
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="h3" fontWeight={800}>
                          {donation?.title}
                        </Typography>
                        {donation?.description ? (
                          <Typography
                            sx={{ mt: 1.25, maxWidth: 820 }}
                            variant="body1"
                            color="text.secondary"
                          >
                            {donation.description}
                          </Typography>
                        ) : null}
                        {donation?.donation_created_user_name ? (
                          <Typography
                            sx={{ mt: donation?.description ? 0.75 : 1 }}
                            variant="caption"
                            color="text.secondary"
                          >
                            {t('donations.created-by')}{' '}
                            {donation.donation_created_user_name}
                          </Typography>
                        ) : null}
                      </Box>
                      <Stack
                        direction="row"
                        flexWrap="wrap"
                        justifyContent={{ xs: 'flex-start', sm: 'flex-end' }}
                        gap={1}
                      >
                        <Chip
                          label={
                            isPartial
                              ? t('donations.partial-donation-label', {
                                  defaultValue: 'Partial donation',
                                })
                              : t('donations.DONATION')
                          }
                          variant="outlined"
                          sx={{
                            borderRadius: 1,
                            fontWeight: 700,
                            textTransform: 'none',
                          }}
                        />
                        <Chip
                          label={donationTypeLabel}
                          sx={{
                            bgcolor: 'grey.100',
                            borderRadius: 1,
                            color: 'text.primary',
                            fontWeight: 800,
                          }}
                        />
                        {recurringSchedule ? (
                          <Chip
                            icon={<EventRepeatRounded />}
                            label={t('donations.recurring', {
                              defaultValue: 'Recurring',
                            })}
                            sx={{
                              bgcolor: 'grey.100',
                              borderRadius: 1,
                              color: 'text.primary',
                              fontWeight: 800,
                              '& .MuiChip-icon': {
                                color: 'text.secondary',
                              },
                            }}
                          />
                        ) : null}
                      </Stack>
                    </Stack>
                  </Box>

                  <Box
                    sx={{
                      display: 'grid',
                      gap: 1.25,
                      gridTemplateColumns: {
                        xs: '1fr',
                        md: 'minmax(0, 1.3fr) minmax(220px, 0.7fr)',
                      },
                    }}
                  >
                    <Box
                      sx={{
                        bgcolor: 'grey.100',
                        borderRadius: 1,
                        minHeight: 112,
                        px: 2,
                        py: 1.75,
                      }}
                    >
                      <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                        spacing={1}
                      >
                        <Typography variant="caption" color="text.secondary">
                          {t('donations.pickup-window', {
                            defaultValue: 'Pickup window',
                          })}
                        </Typography>
                      </Stack>
                      <Typography
                        variant="h5"
                        sx={{ mt: 0.5, fontWeight: 850, lineHeight: 1.25 }}
                      >
                        {donationWindow.date}
                      </Typography>
                      {donationWindow.time ? (
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mt: 0.75 }}
                        >
                          {donationWindow.time}
                        </Typography>
                      ) : null}
                      {recurringSchedule ? (
                        <Box
                          sx={{
                            borderColor: 'divider',
                            borderTop: '1px solid',
                            mt: 1.5,
                            pt: 1.25,
                          }}
                        >
                          <Stack
                            alignItems={{ xs: 'stretch', sm: 'center' }}
                            direction={{ xs: 'column', sm: 'row' }}
                            justifyContent="space-between"
                            spacing={1}
                          >
                            <Box sx={{ minWidth: 0 }}>
                              <Stack
                                alignItems="center"
                                direction="row"
                                spacing={0.75}
                              >
                                <EventRepeatRounded
                                  sx={{
                                    color: 'text.secondary',
                                    fontSize: 18,
                                  }}
                                />
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  {t('donations.repeats', {
                                    defaultValue: 'Repeats',
                                  })}
                                </Typography>
                              </Stack>
                              <Typography
                                variant="body2"
                                sx={{ mt: 0.25 }}
                                fontWeight={800}
                              >
                                {recurringSchedule.detail ||
                                  recurringSchedule.frequency}
                              </Typography>
                              {recurringSchedule.isCancelled ? (
                                <Chip
                                  label={t('donations.series-cancelled', {
                                    defaultValue: 'Series cancelled',
                                  })}
                                  size="small"
                                  sx={{
                                    bgcolor: 'grey.200',
                                    borderRadius: 1,
                                    color: 'text.secondary',
                                    fontWeight: 700,
                                    mt: 1,
                                  }}
                                />
                              ) : null}
                            </Box>
                            {canCancelRecurringDonation ? (
                              <Button
                                color="inherit"
                                onClick={() =>
                                  setShowRecurringCancelOptions(true)
                                }
                                size="small"
                                startIcon={<EventBusyRounded />}
                                sx={{
                                  borderColor: 'divider',
                                  color: 'text.secondary',
                                  flexShrink: 0,
                                  fontWeight: 800,
                                  textTransform: 'none',
                                }}
                                variant="outlined"
                              >
                                {t('donations.cancel', {
                                  defaultValue: 'Cancel',
                                })}
                              </Button>
                            ) : null}
                          </Stack>
                        </Box>
                      ) : null}
                    </Box>
                    <Box
                      sx={{
                        bgcolor: 'grey.100',
                        borderRadius: 1,
                        minHeight: 112,
                        px: 2,
                        py: 1.75,
                      }}
                    >
                      <Typography variant="caption" color="text.secondary">
                        {t('donations.vehicle-needed', {
                          defaultValue: 'Vehicle needed',
                        })}
                      </Typography>
                      <Typography
                        variant="body1"
                        sx={{ mt: 0.5 }}
                        fontWeight={850}
                      >
                        {vehicleLabel}
                      </Typography>
                    </Box>
                  </Box>
                </Stack>
              </DetailCard>

              <FoodAvailabilityCard
                rows={foodLedgerRows}
                isPartial={isPartial}
                defaultUnit={defaultWeightUnit?.value}
                isSingleRescueView={isSingleRescueView}
                isDonorTeam={isDonorTeam}
                action={donationAmountsAction}
                t={t}
              />

              {hasDonationPhotos ? (
                <DetailCard
                  title={t('donations.photos')}
                  action={
                    user?.is_non_profit &&
                    user?.team_id === donation?.donor_team_id ? (
                      <Button
                        onClick={() => setShowUploadPhoto(true)}
                        variant="text"
                      >
                        <AddAPhotoRounded sx={{ mr: 1 }} />
                        {t('donations.add-photo')}
                      </Button>
                    ) : null
                  }
                >
                  <Stack spacing={2}>
                    {foodImages.length > 0 ? (
                      <Box>
                        <DonationImageGrid
                          images={foodImages.map(
                            (key) => `${S3_BASE_URL}/${BUCKET_NAME}/${key}`
                          )}
                        />
                        <StockPhotoDisclaimer
                          images={foodImages}
                          component="p"
                          mt={1}
                        />
                      </Box>
                    ) : null}

                    {additionalImages.length > 0 && (
                      <Box>
                        <TitleInfo component="h3" variant="subtitle1">
                          {t('donations.additional-information')}
                        </TitleInfo>
                        <DonationImageGrid
                          mt={1}
                          images={additionalImages.map(
                            (key) => `${S3_BASE_URL}/${BUCKET_NAME}/${key}`
                          )}
                        />
                      </Box>
                    )}
                    {!!donation?.rescue_id && rescueImages.length > 0 ? (
                      <Box>
                        <TitleInfo component="h3" variant="subtitle1">
                          {t('donations.rescue-images')}
                        </TitleInfo>
                        <Box mt={1}>
                          <DonationImageGrid images={rescueImages} />
                        </Box>
                      </Box>
                    ) : null}
                  </Stack>
                </DetailCard>
              ) : null}

              {!isMobileLayout ? activityLogCard : null}
            </Stack>
          </Grid>

          <Grid size={{ xs: 12, md: 4.5 }}>
            <Stack spacing={2.5}>
              <DetailCard>
                <Menu
                  anchorEl={anchorEl}
                  keepMounted
                  open={!!anchorEl}
                  onClose={() => setAnchorEl()}
                >
                  <MenuItem onClick={handleDelete}>
                    {t('donations.delete')}
                  </MenuItem>
                  <Divider />
                  <MenuItem onClick={() => setAnchorEl()}>
                    {t('donations.close-menu')}
                  </MenuItem>
                </Menu>

                <Stack
                  direction={{ xs: 'column-reverse', lg: 'row' }}
                  alignItems={{ xs: 'stretch', lg: 'flex-start' }}
                  justifyContent="space-between"
                  spacing={1.5}
                  sx={{ gap: 1.5 }}
                >
                  <Box sx={{ flex: '1 1 auto', minWidth: 0 }}>
                    {donationActionControls || (
                      <Typography variant="body2" color="text.secondary">
                        {isReadOnly
                          ? t('donations.view-only-status', {
                              defaultValue:
                                'This account can view details only.',
                            })
                          : t('donations.no-actions-for-status', {
                              defaultValue:
                                'No actions are available for this status.',
                            })}
                      </Typography>
                    )}
                  </Box>
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent={{ xs: 'flex-start', lg: 'flex-end' }}
                    spacing={1}
                  >
                    <StatusBadge
                      status={donation?.rescue_status}
                      label={getStatus(donation?.rescue_status)}
                    />
                    {user.account_type === 'ADMIN' ? (
                      <IconButton
                        onClick={(e) => setAnchorEl(e.currentTarget)}
                        size="small"
                      >
                        <MoreVert />
                      </IconButton>
                    ) : null}
                  </Stack>
                </Stack>

                <Divider sx={{ my: 1.5 }} />

                <Stack spacing={1.5} divider={<Divider />}>
                  {donation?.delivery_option !== 'STAFF-MEALS' &&
                  donation.rescue_drivers?.length ? (
                    <Box>
                      <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                        spacing={1}
                      >
                        <Typography variant="caption" color="text.secondary">
                          {t('donations.drivers')}
                        </Typography>
                        {canReassignRescue ? (
                          <AcceptDonation
                            onAccept={handleAcceptRescue}
                            donation={donation}
                            loading={acceptLoading}
                            trainingComplete={trainingComplete}
                            size="small"
                            variant="outlined"
                            sx={{
                              borderRadius: 1,
                              flexShrink: 0,
                              minHeight: 32,
                            }}
                          />
                        ) : null}
                      </Stack>
                      <Stack mt={0.75} spacing={0.75}>
                        {donation.rescue_drivers.map((driver) => {
                          const isCurrentDriver =
                            String(driver.user_id) === String(user.id);

                          return (
                            <Stack
                              key={driver.user_id}
                              direction={{ xs: 'column', sm: 'row' }}
                              alignItems={{ xs: 'flex-start', sm: 'center' }}
                              justifyContent="space-between"
                              spacing={1}
                            >
                              <Box sx={{ minWidth: 0 }}>
                                <Typography variant="body2" fontWeight={700}>
                                  {driver.user_name}
                                </Typography>
                                {driver.vehicle_name ? (
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                  >
                                    {t('donations.vehicle')}:{' '}
                                    {driver.vehicle_name}
                                  </Typography>
                                ) : null}
                              </Box>
                              {canDriverSelfUnassign && isCurrentDriver ? (
                                <DriverSelfUnassignButton
                                  donation={donation}
                                  onUnassign={() => {
                                    handleRescueStatusChange();
                                    history.push(PATH_DASHBOARD.myrescues);
                                  }}
                                />
                              ) : null}
                            </Stack>
                          );
                        })}
                      </Stack>
                    </Box>
                  ) : null}

                  <Box>
                    <Stack
                      direction="row"
                      alignItems="center"
                      justifyContent="space-between"
                      spacing={1}
                    >
                      <Typography variant="caption" color="text.secondary">
                        {t('donations.donor-info')}
                      </Typography>
                      {showChatWithDonorInfo ? (
                        <ChatWithParticipants donation={donation} />
                      ) : null}
                    </Stack>
                    {isDonorHidden(donation) ? (
                      <>
                        <Typography variant="body2" fontWeight={700}>
                          {getHiddenDonorLabel(donation, t)}
                        </Typography>
                        <Stack
                          mt={1}
                          direction="row"
                          alignItems="flex-start"
                          spacing={1}
                        >
                          <HomeOutlined fontSize="small" />
                          <Typography sx={{ flex: 1 }} variant="body2">
                            {donorMapLocation?.formatted_address ||
                              t('common.na')}
                          </Typography>
                        </Stack>
                        <Stack
                          mt={0.75}
                          direction="row"
                          alignItems="flex-start"
                          spacing={1}
                        >
                          <LockOutlined fontSize="small" color="action" />
                          <Typography
                            sx={{ flex: 1 }}
                            variant="body2"
                            color="text.secondary"
                          >
                            {t('donations.donor-hidden-until-claim')}
                          </Typography>
                        </Stack>
                      </>
                    ) : (
                      <>
                        <Typography variant="body2" fontWeight={700}>
                          {user.team_id === donation?.donor_team_id ? (
                            donation?.donor_business_name
                          ) : (
                            <Link
                              component={RouterLink}
                              to={viewOrg(donation?.donor_team_id)}
                              underline="hover"
                            >
                              {donation?.donor_business_name}
                            </Link>
                          )}
                        </Typography>
                        <Stack
                          mt={1}
                          direction="row"
                          alignItems="center"
                          spacing={1}
                        >
                          <LocalPhoneOutlined fontSize="small" />
                          <Typography sx={{ flex: 1 }} variant="body2">
                            {donation?.donor_business_phone || t('common.na')}
                          </Typography>
                        </Stack>
                        <Stack
                          mt={0.75}
                          direction="row"
                          alignItems="flex-start"
                          spacing={1}
                        >
                          <HomeOutlined fontSize="small" />
                          <Typography sx={{ flex: 1 }} variant="body2">
                            <AddressLink location={donorMapLocation}>
                              {donorMapLocation?.formatted_address ||
                                t('common.na')}
                            </AddressLink>
                          </Typography>
                        </Stack>
                      </>
                    )}
                  </Box>

                  {shouldShowRecipientInfo ? (
                    <Box>
                      <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                        spacing={1}
                      >
                        <Typography variant="caption" color="text.secondary">
                          {t('donations.recipient')}
                        </Typography>
                        {showChatWithRecipientInfo ? (
                          <ChatWithParticipants donation={donation} />
                        ) : null}
                      </Stack>
                      <Typography variant="body2" fontWeight={700}>
                        {donation?.recipient_team_id ? (
                          user.team_id === donation?.recipient_team_id ? (
                            donation?.recipient_business_name
                          ) : (
                            <Link
                              component={RouterLink}
                              to={viewOrg(donation?.recipient_team_id)}
                              underline="hover"
                            >
                              {donation?.recipient_business_name}
                            </Link>
                          )
                        ) : (
                          t('common.na')
                        )}
                      </Typography>
                      <Stack
                        mt={1}
                        direction="row"
                        alignItems="center"
                        spacing={1}
                      >
                        <LocalPhoneOutlined fontSize="small" />
                        <Typography sx={{ flex: 1 }} variant="body2">
                          {donation?.rescue_team_phone || t('common.na')}
                        </Typography>
                      </Stack>
                      <Stack
                        mt={0.75}
                        direction="row"
                        alignItems="flex-start"
                        spacing={1}
                      >
                        <HomeOutlined fontSize="small" />
                        <Typography sx={{ flex: 1 }} variant="body2">
                          <AddressLink location={recipientMapLocation}>
                            {recipientMapLocation?.formatted_address ||
                              t('common.na')}
                          </AddressLink>
                        </Typography>
                      </Stack>
                    </Box>
                  ) : null}

                  {donation?.food_mover_business_name ? (
                    <DetailLine
                      label={t('donations.food-mover')}
                      value={donation.food_mover_business_name}
                    />
                  ) : null}
                </Stack>

                {instructionItems.length ? (
                  <>
                    <Divider sx={{ my: 1.5 }} />
                    <Stack spacing={1.5}>
                      {instructionItems.map((item) => (
                        <Box key={item.label}>
                          <Typography variant="caption" color="text.secondary">
                            {item.label}
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{
                              mt: 0.5,
                              overflowWrap: 'break-word',
                              whiteSpace: 'pre-wrap',
                            }}
                          >
                            {item.value}
                          </Typography>
                        </Box>
                      ))}
                    </Stack>
                  </>
                ) : null}
              </DetailCard>

              <DetailCard sx={{ overflow: 'hidden', p: 0 }}>
                {donation?.is_food_run ? (
                  <Box sx={{ height: { xs: 280, md: 320 }, width: '100%' }}>
                    <GoogleDirectionsMap
                      sourceLocation={foodMoverLocation}
                      destinationLocation={receiverLocation}
                      waypoints={[donorLocation]}
                    />
                  </Box>
                ) : (
                  <Box
                    sx={{
                      height: { xs: 280, md: 320 },
                      position: 'relative',
                      width: '100%',
                    }}
                  >
                    {!donation?.recipient_team_id ? (
                      <Box sx={{ height: '100%', width: '100%' }}>
                        <GoogleMapMarker
                          sx={{
                            zIndex: 0,
                            height: '100%',
                            width: '100%',
                            overflow: 'hidden',
                            position: 'relative',
                          }}
                          pos={{
                            lat: isDestLocationPresent
                              ? donation?.parsed_dest_location?.lat
                              : sourceLocation?.lat,
                            lng: isDestLocationPresent
                              ? donation?.parsed_dest_location?.lng
                              : sourceLocation?.lng,
                          }}
                          areaRadiusMeters={
                            isDestLocationPresent
                              ? null
                              : getApproximateRadius(donation)
                          }
                        />
                      </Box>
                    ) : null}
                    {donation?.recipient_team_id ? (
                      <>
                        <GoogleDirectionsMap
                          sourceLocation={sourceLocation}
                          destinationLocation={destinationLocation}
                          waypoints={[]}
                        />
                        <Tooltip title={t('donations.view-directions')}>
                          <IconButton
                            size="small"
                            sx={{
                              bgcolor: 'background.paper',
                              borderRadius: 1,
                              boxShadow: 2,
                              color: 'primary.main',
                              position: 'absolute',
                              right: 12,
                              top: 12,
                              zIndex: 1,
                              '&:hover': {
                                bgcolor: 'background.paper',
                              },
                            }}
                            onClick={() => {
                              const URL = `https://www.google.com/maps/dir/?api=1&origin=${sourceLocation.formatted_address}&origin_place_id=${sourceLocation.place_id}&destination=${destinationLocation.formatted_address}&destination_place_id=${destinationLocation.place_id}`;

                              window.open(
                                URL + '&travelmode=driving&dir_action=navigate',
                                '_blank'
                              );
                            }}
                          >
                            <OpenInNew fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </>
                    ) : null}
                  </Box>
                )}
                {mapInfoItems.length ? (
                  <Box
                    sx={{
                      borderTop: (theme) =>
                        `1px solid ${theme.palette.divider}`,
                      px: 1.5,
                      py: 1.25,
                    }}
                  >
                    <Stack spacing={1}>
                      {mapInfoItems.map((item) => (
                        <Box key={item.label}>
                          <Typography variant="caption" color="text.secondary">
                            {item.label}
                          </Typography>
                          <Typography variant="body2" fontWeight={700}>
                            {item.location ? (
                              <AddressLink location={item.location}>
                                {item.value}
                              </AddressLink>
                            ) : (
                              item.value
                            )}
                          </Typography>
                        </Box>
                      ))}
                    </Stack>
                  </Box>
                ) : null}
              </DetailCard>

              {!isDonorHidden(donation) ? (
                <DonationNotes
                  donation={donation}
                  user={user}
                  donationId={donationId}
                  hideWhenEmpty
                  sx={{ mt: 0, p: 2.5, ...detailCardSx }}
                />
              ) : null}
            </Stack>
          </Grid>
        </Grid>
      )}
      {!isLoading && isMobileLayout ? (
        <Box sx={{ mt: 2.5 }}>{activityLogCard}</Box>
      ) : null}

      <>
        <RecurringCancelOptionsDialog
          canCancelFuture={canCancelFutureRecurringDonations}
          onCancelCurrent={handleCancelCurrentRecurringDonation}
          onCancelFuture={handleCancelFutureRecurringDonations}
          onClose={() => setShowRecurringCancelOptions(false)}
          open={showRecurringCancelOptions}
          t={t}
        />
        <ConfirmModal
          open={showCancelConfirmModal}
          handleCancel={() => setShowCancelConfirmModal(false)}
          onClick={handleCancelFutureDonations}
          confirmMessage={t(
            'donations.this-will-cancel-all-incomplete-future-instances',
            {
              context: 'donation',
              date: formatDisplayDate(
                moment().tz(timeZoneId ?? 'America/Los_Angeles'),
                'dateTime'
              ),
            }
          )}
          okLabel={t('donations.cancel-future-donations')}
          cancelLabel={t('donations.keep-future-donations')}
        />
        <ConfirmModal
          open={showCancelSingleInstanceDonationConfirmModal}
          handleCancel={() =>
            setShowCancelSingleInstanceDonationConfirmModal(false)
          }
          onClick={handleCancelSingleInstanceDonation}
          confirmMessage={t('donations.this-will-cancel-this-donation-only')}
          okLabel={t('donations.cancel-donation')}
          cancelLabel={t('donations.keep-donation')}
        />
        <DonationCancelConfirmationModal
          open={showCancelDonationConfirmModal}
          handleCancel={() => setShowCancelDonationConfirmModal(false)}
          donation={donation}
          allRescues={allRescues}
          title={
            donation?.is_recurring
              ? t('donations.cancel-this-pickup', {
                  defaultValue: 'Cancel this pickup',
                })
              : undefined
          }
        />
        <DonationCancelConfirmationModal
          open={showCancelRecurringDonationConfirmModal}
          handleCancel={() => setShowCancelRecurringDonationConfirmModal(false)}
          donation={donation}
          allRescues={allRescues}
          type="recurring"
          title={t('donations.cancel-future-pickups', {
            defaultValue: 'Cancel future pickups',
          })}
        />
        {driverSelectorOpen && (
          <DriverSelectorModal
            open={driverSelectorOpen}
            donation={donation}
            onClose={() => setDriverSelectorOpen(false)}
            handleDriverAssign={handleDriverAssign}
            setCurrentUserAsDefault={true}
          />
        )}
        <EditConfirmModal
          open={showEditConfirmModal}
          handleClose={() => setShowEditConfirmModal(false)}
          handleEditFutureDonations={handleEditFutureDonations}
          handleEditDonationSingleInstance={handleEditDonationSingleInstance}
        />
        <RepostConfirmModal
          open={showRepostConfirmModal}
          handleClose={() => setShowRepostConfirmModal(false)}
          handleRepost={() => {
            setShowCreateDonationDialog(true);
            setRepost(true);
            setShowRepostConfirmModal(false);
          }}
        />
        <ConfirmModal
          open={showCopyConfirmModal}
          handleCancel={() => setShowCopyConfirmModal(false)}
          onClick={handleCopyDonation}
          confirmMessage={t('donations.confirm-copy-donation')}
          okLabel={t('common.yes')}
          cancelLabel={t('common.no')}
          okButtonColor="primary"
          okButtonVariant="contained"
        />
        <CompleteFoodRunModal
          partialRescues={partialRescues.filter(
            (r) => r.rescue_id === rescueId
          )}
          open={showCompleteModal}
          onClose={() => setShowCompleteModal(false)}
          donorBusinessName={donation?.donor_business_name}
          receiverBusinessName={donation?.recipient_business_name}
          handleComplete={handleComplete}
          rescueId={donation?.rescue_id}
          handleUpdateCompletionData={handleUpdateCompletionData}
          foodWeights={foodWeights}
          images={parseJsonArray(donation?.completion_images)}
          isCompleted={donation?.rescue_status === 'COMPLETED'}
          rescuedFoodWeights={rescuedFoodWeights}
          isRecipientSameAsFoodMover={isRecipientSameAsFoodMover}
          isLoading={updateCompletionDataLoading}
          startLoading={() => setUpdateCompletionDataLoading(true)}
          stopLoading={() => setUpdateCompletionDataLoading(false)}
          isFoodRun={donation?.is_food_run}
          notes={donation?.notes}
          weightsEnteredByUserId={donation?.weights_entered_by_user_id}
          weightsEnteredByUserName={donation?.weights_entered_by_user_name}
          donation={donation}
        />
      </>
    </>
  );
}
